import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useAppSelector, useAppDispatch } from "hooks/redux";
import { useTranslation } from "react-i18next";
import useModal from "hooks/modals/useModal";
import axios, { CancelToken, CancelTokenSource } from "axios";
import { chunk, cloneDeep, debounce, groupBy, range, take } from "lodash";

import { GetLeague } from "api/rpc/2024-04/facilityAdmin/league/league";
import { GetTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import {
  GetTeeTime,
  GetBookingCategory,
  DeleteTeeTime,
  ImportDrawsheet,
  PostTeeTimeSqueeze,
  GetTeeTimeCartsAvailable,
  IGetTeeTimeCartsAvailableResponse,
  AddTournamentPlayer,
  ImportPlayersFile,
  PostTeeTimeLottery,
  GetTeeTimeLottery,
  PutTeeTimeLottery,
  PutTeeTimeLotteryRequest,
  PostTeeTimeLotteryBuild,
  GetTeeTimeLotteryRequest,
  DeleteTeeTimeLotteryRequest,
  PostTeeTimeLotteryFinalize,
} from "api/rpc/2024-04/facilityAdmin/teesheet/teeSheet";
import {
  PutCancelBooking,
  PutCancelParticipant,
  PutBookingParticipantNoShow,
  GetBooking,
  PutMoveBooking,
  PutCheckAllParticipant,
  PutBookingConfirmation,
  PutBookingParticipant,
  PostNoShowEmail,
} from "api/rpc/2024-04/facilityAdmin/teesheet/booking";
import { PostTeeTimeLock } from "api/rpc/facilityAdmin/teesheet/teetime";
import { GetShotGunTemplate } from "api/rpc/2024-04/facilityAdmin/teesheet/template";
import { PutEditShotgunApply, RemoveShotgunTemplate } from "api/rpc/2024-04/facilityAdmin/teesheet/shotgun";
import { IAPIResponse, StatusCode } from "api/protocols";

import { selectTeeTimeSuccess, selectTeeTime } from "redux/actions/teesheet";
import {
  showError,
  showSuccess,
  initiateNewBooking,
  initiateEditBooking,
  enqueue,
  dequeue,
  initiateNewLotteryRequest,
} from "redux/actions/ui";

import { valueToString, calculateHexColorBrightness, formatDate, errorMessage } from "helpers/Helpers";
import { isTurnBlockedForEighteenHoles } from "helpers/teeSheetTurn";

import Spin from "components/spin/spin";
import Portal from "elements/Portal";
import Popup from "components/popup/Popup";
import BookingModalNew from "./modals/BookingModalNew";
import BookingModalEdit from "./modals/BookingModalEdit";
import { Select } from "components/select/index";
import CreateShotgunModal from "./modals/CreateShotgunModal";
import AuditTrailModal from "./modals/AuditTrailModal";
import TeeTimeDetailsModal from "./modals/TeeTimeDetailsModal";
import AddTeeTimeModal from "./modals/AddTeeTimeModal";
import EditStartHoleModal from "./modals/EditStartHoleModal";
import ImportDrawSheetModal from "./modals/ImportDrawSheetModal";
import PrintCartSignsModal from "./modals/PrintCartSignsModal";
import PowerCartNumbersModal from "./modals/PowerCartNumbersModal";
import IssueRainCheckModal from "./modals/IssueRainCheckModal";
import Callout from "components/callout/Callout";

import { IDivision } from "redux/reducers/models/facility";
import { ILeague } from "redux/reducers/models/league";
import { ITournament } from "redux/reducers/models/tournament";
import {
  ITeeTimeNew,
  ITeeTimeSlotNew,
  IBookingCategory,
  ITeeTimeBookingCategory,
  IBookingParticipant,
  ITeeTime,
  IBooking,
} from "redux/reducers/models/teetime";
import { TShotgunTemplate } from "redux/reducers/models/teesheet";

import {
  BeforeCapture,
  DragDropContext,
  DragStart,
  Draggable,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";

import "./newTeeSheetTabs.scss";
import Checkbox from "components/form/checkbox/Checkbox";
import Sheet from "components/sheet/Sheet";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { IProduct } from "redux/reducers/models/product";
import { LocaleCurrency } from "helpers/Locale";
import useCancelToken from "hooks/useCancelToken/useCancelToken";
import { PostLineItemsToOrder, PostOrder } from "api/rpc/2024-04/facilityAdmin/order/order";
import { PostTransaction, PutCapture } from "api/rpc/2024-04/facilityAdmin/order/transaction";
import { GetCustomer, GetCustomerPaymentMethod } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { useStripe } from "@stripe/react-stripe-js";
import {
  loadingTeetimeTabActionsAreEqual,
  loadingTeetimeTabActionsContainsId,
  TLoadingTeetimeTabsActionStatus,
  TLoadingAddBookingsAction,
  TNewTeeSheetLoadingTeetimeTabAction,
  TLoadingMoveBookingsAction,
  TLoadingUpdateBookingAction,
} from "./loadingTeetimeTabActions";
import { TeesheetActionTypes } from "redux/actions/actionTypes";
import { ISideBySideTeeSheetDivision, ITeeTimeTabNew } from "./NewTeeSheet";
import NewTeeSheetTabsDivision, { NewTeeSheetTabsDivisionUpdateModalType } from "./NewTeeSheetTabsDivision";
import {
  ITeeSheetSideBySideSettings,
  IUpdateTeeSheetSideBySideSettings,
  TTeeSheetView,
} from "hooks/useTeeSheetSideBySideSettings/useTeeSheetSideBySideSettings";
import { GetEventScorecards } from "api/rpc/2024-04/facilityAdmin/event/scorecard";
import AddTournamentPlayerModal from "./modals/AddTournamentPlayer";
import { ICustomer } from "redux/reducers/models/customer";
import ImportPlayersFileModal from "./modals/ImportPlayersFileModal";
import FormLayout from "components/form/FormLayout";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import TimePick from "components/timePick/TimePick";
import { ILottery, ILotteryRequest } from "redux/reducers/models/lottery";
import SelectNew from "components/select/SelectNew";
import LotteryRequestModal from "./modals/LotteryRequestModal";
import ReactDOM from "react-dom";
import { ButtonNew as Button } from "components/buttonNew";
import classNames from "classnames";
import GolferCard from "components/bookingPopUp/golferCard/GolferCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation, faPenToSquare } from "@fortawesome/pro-regular-svg-icons";
import { Badge } from "components/badge/Badge";
import Toggle from "components/form/toggle/Toggle";
import moment from "moment";
import PrintScoreCardsModal from "./modals/PrintScoreCardsModal";
import DuplicateBookingModal from "components/duplicateBookingModal/DuplicateBookingModal";
import EditShotgunModal from "./modals/EditShotgunModal";

interface INewTeeSheetTabs {
  teeSheetView: TTeeSheetView;
  sideBySideTeeSheetDivisions: ISideBySideTeeSheetDivision[];
  allDivisions: IDivision[];
  teetimeTabs: ITeeTimeTabNew[];
  teetimeBookingCategories: ITeeTimeBookingCategory[][];
  reloadTeeTimeTabs: () => void;
  reloadActionTeeTimeTabs: (
    loadingTeetimeTabAction: TNewTeeSheetLoadingTeetimeTabAction,
    useLoader: boolean,
  ) => Promise<TLoadingTeetimeTabsActionStatus>;
  debouncedTeeTimeSearch: string;
  //Manually set the tee times in state
  setTeeTimes?: (teeTimes: Array<ITeeTimeNew>) => void;
  loadingTeetimeTabActions: TNewTeeSheetLoadingTeetimeTabAction[];
  setLoadingTeetimeTabActions: React.Dispatch<React.SetStateAction<TNewTeeSheetLoadingTeetimeTabAction[]>>;
  jumpToTime: string;
  autoScrollNavigationKeyPrefix: string;
  preferredDivisionId?: number;
  sideBySideSettings: ITeeSheetSideBySideSettings;
  updateSideBySideSettings: IUpdateTeeSheetSideBySideSettings;
  sideBySideSettingsKeyPrefix: string | undefined;
  onBookingDragActiveChange?: (active: boolean) => void;
}

export interface INewTeeSheetTabsState {
  sortedTeeTimes: ITeeTimeTabNew[];
  groupedTeeTimes: ITeeTimeDraggable[];
  selectedTeeTime: ITeeTimeTabNew;
  teeSheetLeagues: ILeague[];
  teeSheetTournaments: ITournament[];
  teeSheetLotteries: ILottery[];
}

type TGroupedSideBySideTeeSheetDivision = ISideBySideTeeSheetDivision & {
  sortedTeeTimes: ITeeTimeTabNew[];
  groupedTeeTimes: ITeeTimeDraggable[];
};

interface IDragAndDropMoveGroup {
  sourceTeeTime: ITeeTimeDraggable;
  destTeeTime: ITeeTimeDraggable;
}

export interface INewTeeSheetTabsDragAndDropState {
  teetimeToolTipDisabled: boolean;
  foundSourceTeeTime: ITeeTimeDraggable;
  draggingSlotGroup: ITeeTimeSlotNew[];
  moveGroups: IDragAndDropMoveGroup[];
}

type TDraggableSlot = Array<Array<ITeeTimeSlotNew>>;

export interface ITeeTimeDraggable extends Omit<ITeeTimeTabNew, "slots"> {
  slots: TDraggableSlot;
}

interface IMoveTeetimeConfirmationModal {
  teeTimeBookingID: number;
  startTeeTimeID: number;
  destinationTeeTimeID: number;
  displayHoleConversionWarning: boolean;
  draggingBookingHoles: number;
  sendConfirmation: boolean;
}

export interface ICheckInAllShotGunParticipantsModal {
  teetime: ITeeTimeTabNew;
}

export interface ICheckInAllTournamentParticipantsModal {
  teetime: ITeeTimeTabNew;
}

export interface ICheckInAllLeagueParticipantsModal {
  teetime: ITeeTimeTabNew;
}

export interface INoShowModal {
  type: string;
  bookingParticipantIds: number[];
  slotGroup: ITeeTimeSlotNew[];
  teeTimeIndex: number;
  chargeNoShow: boolean;
  bookingId: number;
  sendNoShowChargeEmail: boolean;
}
export interface INoShowChargeModal {
  booking: IBooking;
  selectedProduct: any;
  noShowProducts: IProduct[];
  paymentMethod: {
    stripe_payment_method_id: string;
    last4: string;
  };
  confirmationPopup: boolean;
  sendNoShowChargeEmail: boolean;
}

export interface ICreateShotgunModal {
  teetime: ITeeTimeTabNew;
}

export interface IAuditTrailModal {
  teetime: ITeeTimeTabNew;
  bookingId: number;
  type: "booking" | "teeTime";
}

export interface ITeeTimeDetailsModal {
  teetime: ITeeTimeTabNew;
}

export interface IEditStartHoleModal {
  teetime: ITeeTimeTabNew;
  start_hole: string;
}

export interface IAddTeeTimeModal {
  teetime: ITeeTimeTabNew;
}

export interface IAddSqueezeTimeModal {
  teetime: ITeeTimeTabNew;
}

export interface IFileImportModal {
  shotgun_application_id: number;
}

export interface IImportPlayersFileModal {
  tee_sheet_id: number;
  tournament_id: number;
  league_id: number;
  date: string;
  start_time: string;
  end_time: string;
  organizer: ICustomer;
}

export interface IAddTournamentPlayerModal {
  slot: ITeeTimeSlotNew;
  organizer: ICustomer;
}

export interface IRemoveShotgunPopup {
  teetime: ITeeTimeTabNew;
}

export interface IEditShotgunPopup {
  teetime: ITeeTimeTabNew;
}

export interface ICancelBookingPopup {
  teetimeToCancelDate: string;
  teetimeToCancelTime: string;
  teetimeToCancelBookingId: number;
  send_cancellation_email: boolean;
}

export interface ICancelPlayerPopup {
  bookingParticipantName: string;
  bookingParticipantId: number;
}

export interface IRemoveTeeTimePopup {
  teetime: ITeeTimeTabNew;
}

export interface ICheckInPlayerPopup {
  bookingParticipantName: string;
  bookingParticipantId: number;
  isGuest: boolean;
}

export interface IIssueRainCheckModal {
  bookingId: number;
  bookingParticipantId: number;
  holes: number;
}

export interface IPrintCartSignsModal {
  league_id: number | undefined;
  tournament_id: number | undefined;
  selected_option: number;
  date: string;
  teetime: ITeeTimeTabNew;
  teetime_id: number;
  shotgun_application_id: number;
}

export interface IPowerCartNumbersModal {
  teetime: ITeeTimeTabNew;
}

export interface IPrintScoreCardModal {
  league: ILeague;
  tournament: ITournament;
  logo_display_type: string;
  teetime: ITeeTimeTabNew;
}

export interface ILotteryModal {
  courseId: number;
  date: string;
  openDate: Date;
  closeDate: Date;
  startTime: string;
  endTime: string;
  timePreferences: 1 | 2 | 3;
  allowTimePreferenceWindow: boolean;
  allowGuests: boolean;
}

export interface IEditLotteryModal {
  lotteryId: number;
  openDate: Date;
  closeDate: Date;
  timePreferences: 1 | 2 | 3;
  allowTimePreferenceWindow: boolean;
  allowGuests: boolean;
  tee_time: ITeeTimeTabNew;
}

interface ILotteryBuildState {
  [key: string]: any;
  golferSearchQuery1: string;
  golferSearchQuery2: string;
  golferSearchQuery3: string;
  golferSearchQuery4: string;
  golferSearchResult1: (Partial<ICustomer> | ICustomer)[];
  golferSearchResult2: (Partial<ICustomer> | ICustomer)[];
  golferSearchResult3: (Partial<ICustomer> | ICustomer)[];
  golferSearchResult4: (Partial<ICustomer> | ICustomer)[];
  searching: boolean;
}

export interface ILotteryBuild {
  customer_id: number;
  customer_id_one: number;
  customer_id_two: number;
  customer_id_three: number;
  customer_id_four: number;
  customer_one: ILotteryBuildCustomer;
  customer_two: ILotteryBuildCustomer;
  customer_three: ILotteryBuildCustomer;
  customer_four: ILotteryBuildCustomer;
  lottery_request_id: number;
  quantity: number;
  start_time: string;
  tee_time_id: number;
  lottery_id: number;
}

export interface ILotteryBuildCustomer {
  id: number;
  full_name: string;
  customer_type: string;
}

const DATE_BAR_DROP_DATE_ATTRIBUTE = "data-tee-sheet-drop-date";
const DATE_BAR_DROP_TARGET_CLASS = "date-bar-item--drop-target";

function calendarDay(value: string | null | undefined) {
  return value ? value.slice(0, 10) : "";
}

function sameStartTime(first: string, second: string) {
  return first === second || first.slice(0, 5) === second.slice(0, 5);
}

function clearDateBarDropTarget() {
  document.querySelectorAll(`.${DATE_BAR_DROP_TARGET_CLASS}`).forEach(node => {
    node.classList.remove(DATE_BAR_DROP_TARGET_CLASS);
  });
}

function dateBarItemAtPoint(clientX: number, clientY: number) {
  for (const element of document.elementsFromPoint(clientX, clientY)) {
    if (!(element instanceof Element)) {
      continue;
    }
    const item = element.closest(`[${DATE_BAR_DROP_DATE_ATTRIBUTE}]`);
    if (item instanceof HTMLElement) {
      return item;
    }
  }
  return null;
}

function destinationHoleMove(
  destination: ITeeTime,
  holes: number,
  playerCount: number,
): "wrong-holes" | "unavailable" | "ok" | "convert" {
  if (destination.blocked_type === "crossover" || destination.quantity_remaining < playerCount) {
    return "unavailable";
  }

  const destDivisions = destination.divisions_available ?? [];
  if (!destDivisions.some(division => division === holes / 9)) {
    return "wrong-holes";
  }

  let nineHoleAvailable = false;
  let eighteenHoleAvailable = false;
  const turn = destination.turn_tee_time;

  destDivisions.forEach(division => {
    if (division === 1) {
      nineHoleAvailable = true;
    } else if (division === 2) {
      if (
        (destDivisions.length > 1 && destination.turn_tee_time_id == null) ||
        isTurnBlockedForEighteenHoles(turn?.blocked_type, destination.blocked_type) ||
        ((turn?.league_id || turn?.tournament_id) &&
          (destination.league_id !== turn?.league_id || destination.tournament_id !== turn?.tournament_id))
      ) {
        nineHoleAvailable = true;
      } else if (destination.turn_tee_time_id != null) {
        if ((turn?.quantity_remaining ?? 0) >= playerCount) {
          eighteenHoleAvailable = true;
        } else {
          nineHoleAvailable = true;
        }
      }
    }
  });

  if (!nineHoleAvailable && !eighteenHoleAvailable) {
    return "unavailable";
  }
  if ((holes === 9 && !nineHoleAvailable) || (holes === 18 && !eighteenHoleAvailable)) {
    return "convert";
  }
  return "ok";
}

export function calculateDivisionColors(backgroundHexColor: string): { backgroundColor: string; color: string } {
  const backgroundColorBrightness = calculateHexColorBrightness(backgroundHexColor);

  if (backgroundColorBrightness) {
    return {
      backgroundColor: backgroundHexColor,
      color: backgroundColorBrightness < 50 ? "white" : "black",
    };
  } else {
    return {
      backgroundColor: "#e9e9e9",
      color: "#999999",
    };
  }
}

export default function NewTeeSheetTabs(props: INewTeeSheetTabs) {
  {
    /* CONSTANTS */
  }
  const { facilityStore } = useAppSelector(store => store);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { cancelToken, cancelRequest } = useCancelToken();
  const stripe = useStripe();
  const { Option } = Select;

  const {
    state: duplicateBookingModal,
    updateModal: updateDuplicateBookingModal,
    closeModal: closeDuplicateBookingModal,
  } = useModal<{ booking: IBooking; teeTime: ITeeTime }>({ booking: null, teeTime: null });

  const [lotteryRequest, setLotteryRequest] = useState<ILotteryBuildState>({
    golferSearchQuery1: "",
    golferSearchQuery2: "",
    golferSearchQuery3: "",
    golferSearchQuery4: "",
    golferSearchResult1: [null],
    golferSearchResult2: [null],
    golferSearchResult3: [null],
    golferSearchResult4: [null],
    searching: false,
  });

  const {
    teeSheetView,
    sideBySideTeeSheetDivisions,
    allDivisions,
    setTeeTimes,
    loadingTeetimeTabActions,
    setLoadingTeetimeTabActions,
  } = props;

  const [lotteryBuild, setLotteryBuild] = useState<Array<ILotteryBuild>>([]);

  const [bookingCategories, setBookingCategories] = useState<IBookingCategory[]>(undefined);
  const [shotgunTemplates, setShotgunTemplates] = useState<TShotgunTemplate[]>(undefined);
  const [state, setState] = useState<INewTeeSheetTabsState>({
    sortedTeeTimes: null,
    groupedTeeTimes: null,
    selectedTeeTime: null,
    teeSheetLeagues: undefined,
    teeSheetTournaments: undefined,
    teeSheetLotteries: undefined,
  });

  const [dragAndDropState, setDragAndDropState] = useState<INewTeeSheetTabsDragAndDropState>({
    teetimeToolTipDisabled: false,
    draggingSlotGroup: undefined,
    foundSourceTeeTime: undefined,
    moveGroups: [],
  });
  const dateBarDropDateRef = useRef<string | null>(null);
  const dateDropRequestRef = useRef(0);
  const bookingDragRef = useRef<{
    sourceTeeTime: ITeeTimeDraggable;
    draggingSlotGroup: ITeeTimeSlotNew[];
  } | null>(null);

  const handleDateBarPointerMove = useCallback((event: PointerEvent) => {
    const sourceDate = calendarDay(bookingDragRef.current?.draggingSlotGroup[0]?.date);
    const item = dateBarItemAtPoint(event.clientX, event.clientY);
    const dropDate = calendarDay(item?.getAttribute(DATE_BAR_DROP_DATE_ATTRIBUTE));
    const nextDate = dropDate && dropDate !== sourceDate ? dropDate : null;

    if (dateBarDropDateRef.current === nextDate) {
      return;
    }

    clearDateBarDropTarget();
    dateBarDropDateRef.current = nextDate;
    if (nextDate) {
      item?.classList.add(DATE_BAR_DROP_TARGET_CLASS);
    }
  }, []);

  useEffect(() => {
    return () => {
      dateDropRequestRef.current += 1;
      window.removeEventListener("pointermove", handleDateBarPointerMove);
      clearDateBarDropTarget();
      props.onBookingDragActiveChange?.(false);
    };
  }, [handleDateBarPointerMove, props.onBookingDragActiveChange]);

  {
    /* MODALS */
  }
  const { state: newBookingModal, updateModal: updateNewBookingModal } = useModal({
    clearOnClose: true,
    loading: false,
    loadingText: "",
    postingLock: false,
    slot: null,
    lottery: null,
    lotteryTeeTimes: null,
    cartsAvailable: {
      nineHole: 0,
      eighteenHole: 0,
    },
    groupBookingId: null,
  });

  const {
    state: noShowModal,
    updateModal: updateNoShowModal,
    closeModal: closeNoShowModal,
  } = useModal<INoShowModal>({
    clearOnClose: true,
    type: "charge",
    bookingParticipantIds: [],
    slotGroup: null,
    teeTimeIndex: null,
    chargeNoShow: false,
    bookingId: null,
    sendNoShowChargeEmail: false,
  });

  const {
    state: noShowChargeModal,
    updateModal: updateNoShowChargeModal,
    closeModal: closeNoShowChargeModal,
  } = useModal<INoShowChargeModal>({
    clearOnClose: true,
    booking: null,
    selectedProduct: null,
    noShowProducts: null,
    paymentMethod: null,
    confirmationPopup: false,
    sendNoShowChargeEmail: false,
  });

  const {
    state: createShotgunModal,
    updateModal: updateCreateShotgunModal,
    closeModal: closeCreateShotgunModal,
  } = useModal<ICreateShotgunModal>({
    clearOnClose: true,
    teetime: null,
  });

  const { state: auditTrailModal, updateModal: updateAuditTrailModal } = useModal<IAuditTrailModal>({
    clearOnClose: true,
    teetime: null,
    bookingId: null,
    type: null,
  });

  const {
    state: teeTimeDetailsModal,
    updateModal: updateTeeTimeDetailsModal,
    closeModal: closeTeeTimeDetailsModal,
  } = useModal<ITeeTimeDetailsModal>({
    clearOnClose: true,
    teetime: null,
  });

  const {
    state: editStartHoleModal,
    updateModal: updateEditStartHoleModal,
    closeModal: closeEditStartHoleModal,
  } = useModal<IEditStartHoleModal>({
    clearOnClose: true,
    teetime: null,
    start_hole: "",
  });

  const {
    state: addTeeTimeModal,
    updateModal: updateAddTeeTimeModal,
    closeModal: closeAddTeeTimeModal,
  } = useModal<IAddTeeTimeModal>({
    clearOnClose: true,
    teetime: null,
  });

  const {
    state: addSqueezeTimeModal,
    updateModal: updateAddSqueezeTimeModal,
    closeModal: closeAddSqueezeTimeModal,
  } = useModal<IAddSqueezeTimeModal>({
    clearOnClose: true,
    teetime: null,
  });

  const { state: editBookingModal, updateModal: updateEditBookingModal } = useModal({
    clearOnClose: true,
    selectedTeeTime: null,
    selectedBooking: null,
    teeTimeLock: false,
    loading: false,
    loadingText: "",
    postingLock: false,
    cartsAvailable: {
      nineHole: 0,
      eighteenHole: 0,
    },
  });

  const {
    state: fileImportModal,
    updateModal: updateFileImportModal,
    closeModal: closeFileImportModal,
  } = useModal<IFileImportModal>({ shotgun_application_id: null });

  const {
    state: importPlayersFileModal,
    updateModal: updateImportPlayersFileModal,
    closeModal: closeImportPlayersFileModal,
  } = useModal<IImportPlayersFileModal>({
    tee_sheet_id: null,
    tournament_id: null,
    league_id: null,
    date: "",
    start_time: "",
    end_time: "",
    organizer: null,
  });

  const {
    state: addTournamentPlayerModal,
    updateModal: updateAddTournamentPlayer,
    closeModal: closeAddTournamentPlayer,
  } = useModal<IAddTournamentPlayerModal>({ slot: null, organizer: null });

  const {
    state: printCartSignsModal,
    updateModal: updatePrintCartSignsModal,
    closeModal: closePrintCartSignsModal,
  } = useModal<IPrintCartSignsModal>({
    league_id: undefined,
    tournament_id: undefined,
    selected_option: null,
    date: null,
    teetime: null,
    teetime_id: undefined,
    shotgun_application_id: null,
  });

  const {
    state: powerCartNumbersModal,
    updateModal: updatePowerCartNumbersModal,
    closeModal: closePowerCartNumbersModal,
  } = useModal<IPowerCartNumbersModal>({
    clearOnClose: true,
    teetime: null,
  });

  const {
    state: printScoreCardsModal,
    updateModal: updatePrintScoreCardsModal,
    closeModal: closePrintScoreCardsModal,
  } = useModal<IPrintScoreCardModal>({
    league: undefined,
    tournament: undefined,
    teetime: null,
    logo_display_type: "",
  });

  const {
    state: lotteryModal,
    closeModal: closeLotteryModal,
    updateModal: updateLotteryModal,
  } = useModal<ILotteryModal>({
    courseId: null,
    date: "",
    openDate: new Date(),
    closeDate: new Date(),
    startTime: "",
    endTime: "",
    timePreferences: 1,
    allowTimePreferenceWindow: false,
    allowGuests: true,
  });

  const {
    state: editLotteryModal,
    closeModal: closeEditLotteryModal,
    updateModal: updateEditLotteryModal,
  } = useModal<IEditLotteryModal>({
    tee_time: null,
    openDate: new Date(),
    closeDate: new Date(),
    lotteryId: null,
    timePreferences: 1,
    allowTimePreferenceWindow: false,
    allowGuests: true,
  });

  const {
    state: lotteryRequestsModal,
    closeModal: closeLotteryRequestsModal,
    updateModal: updateLotteryRequestsModal,
  } = useModal({ lottery_id: null, date: "", requests: [] });

  const {
    state: deleteLotteryRequestPopup,
    closeModal: closeDeleteLotteryRequestPopup,
    updateModal: updateDeleteLotteryRequestPopup,
  } = useModal<{ request: ILotteryRequest }>({ request: null });

  const {
    state: buildLotteryPopup,
    closeModal: closeBuildLotteryPopup,
    updateModal: updateBuildLotteryPopup,
  } = useModal({ lotteryId: null, date: "" });

  const {
    state: finalizeLotteryPopup,
    closeModal: closeFinalizeLotteryPopup,
    updateModal: updateFinalizeLotteryPopup,
  } = useModal({ lotteryId: null });

  {
    /* POPUPS */
  }

  const {
    state: noCreditCardPopup,
    updateModal: updateNoCreditCardPopup,
    closeModal: closeNoCreditCardPopup,
  } = useModal({
    clearOnClose: true,
  });

  const {
    state: blockedTeeTimePopup,
    updateModal: updateBlockedTeeTimePopup,
    closeModal: closeBlockedTeeTimePopup,
  } = useModal();

  const {
    state: selectDivisionsPopup,
    updateModal: updateSelectDivisionsPopup,
    closeModal: closeSelectDivisionsPopup,
  } = useModal({
    divisions: facilityStore?.facility?.courses[0]?.divisions,
    selectedFrontNineDivision: null,
    selectedBackNineDivision: null,
    teetime: null,
  });

  const { state: cancelBookingPopup, updateModal: updateCancelBookingPopup } = useModal<ICancelBookingPopup>({
    clearOnClose: true,
    teetimeToCancelDate: "",
    teetimeToCancelTime: "",
    teetimeToCancelBookingId: null,
    send_cancellation_email: true,
  });

  const { state: cancelPlayerPopup, updateModal: updateCancelPlayerPopup } = useModal<ICancelPlayerPopup>({
    clearOnClose: true,
    bookingParticipantName: "",
    bookingParticipantId: null,
  });

  const {
    state: teeTimeBookedPopup,
    updateModal: updateTeeTimeBookedPopup,
    closeModal: closeTeeTimeBookedPopup,
  } = useModal();

  const {
    state: moveTeeTimeBookingConfirmation,
    closeModal: closeMoveTeeTimeBookingConfirmationPopup,
    updateModal: updateMoveTeeTimeBookingConfirmationPopup,
  } = useModal<IMoveTeetimeConfirmationModal>({
    clearOnClose: true,
    teeTimeBookingID: null,
    startTeeTimeID: null,
    destinationTeeTimeID: null,
    displayHoleConversionWarning: false,
    draggingBookingHoles: null,
    sendConfirmation: false,
  });

  const {
    state: teeTimeLockedPopup,
    updateModal: updateTeeTimeLockedPopup,
    closeModal: closeTeeTimeLockedPopup,
  } = useModal();

  const {
    state: removeTeeTimePopup,
    updateModal: updateRemoveTeeTimePopup,
    closeModal: closeRemoveTeeTimePopup,
  } = useModal<IRemoveTeeTimePopup>({
    clearOnClose: true,
    teetime: null,
  });

  const {
    state: removeShotgunPopup,
    updateModal: updateRemoveShotgunPopup,
    closeModal: closeRemoveShotgunPopup,
  } = useModal<IRemoveShotgunPopup>({
    clearOnClose: true,
    teetime: null,
  });

  const {
    state: editShotgunPopup,
    updateModal: updateEditShotgunPopup,
    closeModal: closeEditShotgunPopup,
  } = useModal<IEditShotgunPopup>({
    clearOnClose: true,
    teetime: null,
  });

  const {
    state: shotgunCheckInParticipants,
    updateModal: updateCheckInAllShotgunParticipants,
    closeModal: closeCheckInAllShotgunParticipants,
  } = useModal<ICheckInAllShotGunParticipantsModal>({
    clearOnClose: true,
    teetime: null,
  });

  const {
    state: tournamentCheckInParticipants,
    updateModal: updateCheckInAllTournamentParticipants,
    closeModal: closeCheckInAllTournamentParticipants,
  } = useModal<ICheckInAllTournamentParticipantsModal>({
    clearOnClose: true,
    teetime: null,
  });

  const {
    state: leagueCheckInParticipants,
    updateModal: updateCheckInAllLeagueParticipants,
    closeModal: closeCheckInAllLeagueParticipants,
  } = useModal<ICheckInAllLeagueParticipantsModal>({
    clearOnClose: true,
    teetime: null,
  });

  const {
    state: checkInPlayerPopup,
    updateModal: updateCheckInPlayerPopup,
    closeModal: closeCheckInPlayerPopup,
  } = useModal<ICheckInPlayerPopup>({
    clearOnClose: true,
    bookingParticipantName: "",
    bookingParticipantId: null,
    isGuest: false,
  });

  const {
    state: issueRainCheckModal,
    updateModal: updateIssueRainCheckModal,
    closeModal: closeIssueRainCheckModal,
  } = useModal<IIssueRainCheckModal>({
    clearOnClose: true,
    bookingId: null,
    bookingParticipantId: null,
    holes: null,
  });

  function updateModalState<T>(
    newState: Partial<T> & { isOpen?: boolean },
    type: NewTeeSheetTabsDivisionUpdateModalType,
  ) {
    switch (type) {
      case NewTeeSheetTabsDivisionUpdateModalType.NoShow:
        updateNoShowModal(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.CreateShotgun:
        updateCreateShotgunModal(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.AuditTrail:
        updateAuditTrailModal(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.TeeTimeDetails:
        updateTeeTimeDetailsModal(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.EditStartHole:
        updateEditStartHoleModal(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.AddTeeTime:
        updateAddTeeTimeModal(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.AddSqueezeTime:
        updateAddSqueezeTimeModal(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.FileImport:
        updateFileImportModal(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.RemoveShotgun:
        updateRemoveShotgunPopup(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.EditShotgun:
        updateEditShotgunPopup(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.CheckInAllLeagueParticipants:
        updateCheckInAllLeagueParticipants(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.CheckInAllTournamentParticipants:
        updateCheckInAllTournamentParticipants(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.CheckInAllShotgunParticipants:
        updateCheckInAllShotgunParticipants(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.CancelBooking:
        updateCancelBookingPopup(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.CancelPlayer:
        updateCancelPlayerPopup(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.BlockedTeeTime:
        updateBlockedTeeTimePopup(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.SelectDivisions:
        updateSelectDivisionsPopup(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.RemoveTeeTime:
        updateRemoveTeeTimePopup(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.AddTournamentPlayer:
        updateAddTournamentPlayer(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.PrintCartSigns:
        updatePrintCartSignsModal(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.PowerCartNumbers:
        updatePowerCartNumbersModal(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.PrintScoreCards:
        updatePrintScoreCardsModal(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.ImportPlayersFile:
        updateImportPlayersFileModal(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.Lottery:
        updateLotteryModal(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.LotteryRequests:
        updateLotteryRequestsModal(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.EditLottery:
        updateEditLotteryModal(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.BuildLottery:
        updateBuildLotteryPopup(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.FinalizeLottery:
        updateFinalizeLotteryPopup(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.CheckInPlayer:
        updateCheckInPlayerPopup(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.IssueRainCheck:
        updateIssueRainCheckModal(newState);
        break;
    }
  }

  async function checkInPlayer() {
    const checkInRes = await PutBookingParticipant(
      {
        id: checkInPlayerPopup.bookingParticipantId,
        check_in_status: "checked",
      },
      true,
    );

    if (checkInRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.tee_sheet.tee_sheet_booking_details.173")));
    }

    closeCheckInPlayerPopup();
    void props.reloadTeeTimeTabs();
  }

  {
    /* USE EFFECTS */
  }

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (mounted === true) {
      timeoutId = setTimeout(() => {
        void setLotteryBuildCustomers(mounted, lotteryRequest.golferSearchQuery1, "golferSearchResult1", source.token);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      setLotteryRequest(prev => ({ ...prev, golferSearchResult1: [null] }));
      source.cancel("Cancelled");
    };
  }, [lotteryRequest.golferSearchQuery1]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (mounted === true) {
      timeoutId = setTimeout(() => {
        void setLotteryBuildCustomers(mounted, lotteryRequest.golferSearchQuery2, "golferSearchResult2", source.token);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      setLotteryRequest(prev => ({ ...prev, golferSearchResult2: [null] }));
      source.cancel("Cancelled");
    };
  }, [lotteryRequest.golferSearchQuery2]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (mounted === true) {
      timeoutId = setTimeout(() => {
        void setLotteryBuildCustomers(mounted, lotteryRequest.golferSearchQuery3, "golferSearchResult3", source.token);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      setLotteryRequest(prev => ({ ...prev, golferSearchResult3: [null] }));
      source.cancel("Cancelled");
    };
  }, [lotteryRequest.golferSearchQuery3]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (mounted === true) {
      timeoutId = setTimeout(() => {
        void setLotteryBuildCustomers(mounted, lotteryRequest.golferSearchQuery4, "golferSearchResult4", source.token);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      setLotteryRequest(prev => ({ ...prev, golferSearchResult4: [null] }));
      source.cancel("Cancelled");
    };
  }, [lotteryRequest.golferSearchQuery4]);

  async function setLotteryBuildCustomers(
    mounted: boolean,
    golferSearchQuery: string,
    golferSearchResult: string,
    cancelToken: CancelToken,
  ) {
    try {
      if (golferSearchQuery === "") {
        if (mounted) {
          setLotteryRequest(prev => ({ ...prev, [golferSearchResult]: [null] }));
        }
        return;
      } else {
        setLotteryRequest(prev => ({ ...prev, searching: true }));
        const res = await loadLotteryBuildCustomer(golferSearchQuery, cancelToken);
        if (mounted) {
          setLotteryRequest(prev => ({ ...prev, [golferSearchResult]: res, searching: false }));
        }
      }
    } catch (error) {
      console.log("err", error);
    }
    return;
  }

  async function loadLotteryBuildCustomer(golferSearchQuery: string, cancelToken: CancelToken) {
    const customerRes = await GetCustomer({ search: golferSearchQuery }, false, cancelToken);

    if (customerRes.status !== StatusCode.OK) {
      return [];
    }

    return customerRes.data as Array<Record<string, any>>;
  }

  const teeSheetTabsExitCancelSource = useRef<CancelTokenSource>(axios.CancelToken.source());

  useEffect(() => {
    teeSheetTabsExitCancelSource.current = axios.CancelToken.source();

    return () => teeSheetTabsExitCancelSource.current.cancel();
  }, []);

  function groupSlots(slots: ITeeTimeSlotNew[]) {
    const bookedSlots = slots.filter(slot => slot.tee_time_booking_id != null);
    const nonBookedSlots = slots.filter(slot => slot.tee_time_booking_id == null);

    const groupedBookedSlots = Object.values(groupBy(bookedSlots, s => s.tee_time_booking_id));
    const groupedNonBookedSlots = Object.values(groupBy(nonBookedSlots, s => s.tee_time_slot_id));

    const groupedSlots = [...groupedBookedSlots, ...groupedNonBookedSlots];

    return groupedSlots;
  }

  console.log("sideBySideSettings", props.sideBySideSettings);

  function getSortedAndGroupedTeeTimes(
    teetimeTabs: ITeeTimeTabNew[],
    teeTimeSearch: string,
  ): { sortedTeeTimes: ITeeTimeTabNew[]; groupedTeeTimes: ITeeTimeDraggable[] } {
    if (teetimeTabs == null || teetimeTabs.length === 0) {
      return { sortedTeeTimes: [], groupedTeeTimes: [] };
    }

    let sortedTeeTimes = cloneDeep(teetimeTabs).filter(
      (teetime: ITeeTimeTabNew) => teetime.slots[0].blocked_type !== "shotgun_disabled",
    );

    if (props.sideBySideSettings.hideBlockedTimes) {
      sortedTeeTimes = sortedTeeTimes.filter((teetime: ITeeTimeTabNew) => teetime.slots[0].blocked_type !== "blocked");
    }

    if (props.sideBySideSettings.hideTurnTimes) {
      sortedTeeTimes = sortedTeeTimes.filter(
        (teetime: ITeeTimeTabNew) => teetime.slots[0].blocked_type !== "crossover",
      );
    }

    if (teeTimeSearch !== "") {
      sortedTeeTimes = sortedTeeTimes.filter((teetime: ITeeTimeTabNew) =>
        teetime.slots?.some(slot =>
          `${slot.customer_first_name} ${slot.customer_last_name}`
            .toLocaleLowerCase()
            .includes(teeTimeSearch?.toLocaleLowerCase()),
        ),
      );
    }

    sortedTeeTimes.sort((current, next) => {
      const localCompare = current?.slots[0]?.start_time.localeCompare(next?.slots[0]?.start_time);

      if (localCompare === 0) {
        return current?.slots[0]?.division_id - next?.slots[0]?.division_id;
      } else {
        return localCompare;
      }
    });

    const clonedSortedTeeTimes = cloneDeep(sortedTeeTimes);

    const groupedTeeTimes: ITeeTimeDraggable[] = clonedSortedTeeTimes.map(teetime => {
      return {
        ...teetime,
        slots: groupSlots(teetime.slots),
      };
    });

    return { sortedTeeTimes, groupedTeeTimes };
  }

  const groupedSideBySideDivisions: TGroupedSideBySideTeeSheetDivision[] = useMemo(() => {
    if (teeSheetView === "side-by-side" && sideBySideTeeSheetDivisions?.length > 0) {
      return sideBySideTeeSheetDivisions.map(division => {
        return {
          ...division,
          ...getSortedAndGroupedTeeTimes(division.teetimeTabs, props.debouncedTeeTimeSearch),
        };
      });
    } else {
      return null;
    }
  }, [teeSheetView, sideBySideTeeSheetDivisions, props.debouncedTeeTimeSearch]);

  const sideBySideScrollContainersRef = useRef<Map<number, HTMLDivElement>>(new Map());
  const sideBySideScrollSyncApplyingRef = useRef(false);

  useEffect(() => {
    if (teeSheetView !== "side-by-side" || !props.sideBySideSettings.syncDivisionScroll) {
      sideBySideScrollContainersRef.current.clear();
    }
  }, [teeSheetView, props.sideBySideSettings.syncDivisionScroll]);

  const registerSideBySideScrollContainer = useCallback(
    (divisionId: number, el: HTMLDivElement | null) => {
      if (!props.sideBySideSettings.syncDivisionScroll || teeSheetView !== "side-by-side") {
        if (el == null) {
          sideBySideScrollContainersRef.current.delete(divisionId);
        }
        return;
      }
      if (el) {
        sideBySideScrollContainersRef.current.set(divisionId, el);
      } else {
        sideBySideScrollContainersRef.current.delete(divisionId);
      }
    },
    [props.sideBySideSettings.syncDivisionScroll, teeSheetView],
  );

  const onSideBySideDivisionScroll = useCallback(
    (divisionId: number, event: React.UIEvent<HTMLDivElement>) => {
      if (!props.sideBySideSettings.syncDivisionScroll || teeSheetView !== "side-by-side") {
        return;
      }
      if (sideBySideScrollSyncApplyingRef.current) {
        return;
      }
      const target = event.currentTarget;
      const sourceMax = target.scrollHeight - target.clientHeight;
      const ratio = sourceMax > 0 ? Math.min(1, Math.max(0, target.scrollTop / sourceMax)) : 0;

      sideBySideScrollSyncApplyingRef.current = true;
      sideBySideScrollContainersRef.current.forEach((element, id) => {
        if (id === divisionId || !element) {
          return;
        }
        const maxScroll = element.scrollHeight - element.clientHeight;
        if (maxScroll <= 0) {
          element.scrollTop = 0;
          return;
        }
        element.scrollTop = ratio * maxScroll;
      });
      requestAnimationFrame(() => {
        sideBySideScrollSyncApplyingRef.current = false;
      });
    },
    [props.sideBySideSettings.syncDivisionScroll, teeSheetView],
  );

  // Group slots together by booking id
  useEffect(() => {
    if (!props.teetimeTabs) {
      return;
    }

    const { sortedTeeTimes, groupedTeeTimes } = getSortedAndGroupedTeeTimes(
      props.teetimeTabs,
      props.debouncedTeeTimeSearch,
    );

    updateMainState({ sortedTeeTimes, groupedTeeTimes });
  }, [props.teetimeTabs, props.debouncedTeeTimeSearch]);

  // Load events when tee times are updated to display information about them on the tee sheet
  useEffect(() => {
    const source = axios.CancelToken.source();

    if (!props.teetimeTabs) {
      return;
    }

    const teeTimes: ITeeTimeTabNew[] = [...props.teetimeTabs];

    // Display loader
    updateMainState({ teeSheetTournaments: undefined, teeSheetLeagues: undefined });
    // Load tee sheet tournaments and leagues if they exist
    const tournamentIds = teeTimes
      ?.filter(filteredTeeTime => filteredTeeTime?.slots[0].tournament_id)
      ?.map(teetime => {
        if (teetime.slots[0].tournament_id) {
          return teetime.slots[0].tournament_id;
        }
      });
    void loadTeeSheetTournaments([...new Set(tournamentIds)], source.token);

    const leagueIds = teeTimes
      ?.filter(filteredTeeTime => filteredTeeTime?.slots[0].league_id)
      ?.map(teetime => {
        if (teetime.slots[0].league_id) {
          return teetime.slots[0].league_id;
        }
      });
    void loadTeeSheetLeagues([...new Set(leagueIds)], source.token);

    //Get lottery ids
    const lotteryIds = teeTimes
      ?.filter(filteredTeeTime => filteredTeeTime?.slots[0].lottery_id)
      ?.map(teetime => {
        if (teetime.slots[0].lottery_id) {
          return teetime.slots[0].lottery_id;
        }
      });

    //Load lotteries
    void loadTeeSheetLotteries([...new Set(lotteryIds)], source.token);

    return () => source.cancel();
  }, [props.teetimeTabs]);

  // Load shotgun information
  useEffect(() => {
    const source = axios.CancelToken.source();
    if (!bookingCategories && createShotgunModal.isOpen) {
      void loadBookingCategories(source.token);
    }
    if (!shotgunTemplates && createShotgunModal.isOpen) {
      void loadShotgunTemplates(source.token);
    }
    return () => source.cancel();
  }, [createShotgunModal.isOpen]);

  // Load booking categories for tee time details if they're not already loaded
  useEffect(() => {
    const source = axios.CancelToken.source();
    if (!bookingCategories && teeTimeDetailsModal.isOpen) {
      void loadBookingCategories(source.token);
    }
    return () => source.cancel();
  }, [teeTimeDetailsModal.isOpen]);

  // FUNCTIONS
  function updateMainState(newMainState: Partial<INewTeeSheetTabsState>) {
    setState((cur: INewTeeSheetTabsState) => {
      return { ...cur, ...newMainState };
    });
  }

  async function loadBookingCategories(token?: CancelToken) {
    const bookingCategoryRes = await GetBookingCategory(null, false, token);

    if (token && token.reason) {
      return;
    }

    if (Number(bookingCategoryRes.status) !== Number(StatusCode.OK)) {
      dispatch(showError(t("Error loading booking categories")));
      return;
    }

    setBookingCategories(bookingCategoryRes.data);
  }

  async function loadShotgunTemplates(token?: CancelToken) {
    const templateRes = await GetShotGunTemplate(null, false, token);

    if (token && token.reason) {
      return;
    }
    if (templateRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading shotgun templates")));
    }

    setShotgunTemplates(templateRes.data);
  }

  // TEE TIME FUNCTIONS
  async function removeTeeTime() {
    const params = {
      tee_time_id: removeTeeTimePopup.teetime.slots[0].tee_time_id,
    };

    const removeRes = await DeleteTeeTime(params, true);

    if (Number(removeRes.status) !== Number(StatusCode.OK)) {
      dispatch(showError(t("Error removing shotgun tee time")));
      return;
    }

    dispatch(showSuccess(t("Successfully removed shotgun tee time")));

    closeRemoveTeeTimePopup();

    props.reloadTeeTimeTabs();
  }

  // async function setBlockedType(teetime: any, type: string) {
  //   teetime.blocked_type = type;

  //   const teeTimeRes = await PutTeeTime({ tee_times: [teetime] }, true);

  //   if (teeTimeRes.status !== StatusCode.OK) {
  //     dispatch(showError(t("Error updating tee time")));
  //     return;
  //   }
  // }

  async function createSqueezeTime() {
    const teeTimeRes = await PostTeeTimeSqueeze(
      { tee_time_id: addSqueezeTimeModal.teetime?.slots[0]?.tee_time_id },
      true,
    );

    if (Number(teeTimeRes.status) !== Number(StatusCode.OK)) {
      dispatch(showError(t("Error creating squeeze time")));
      return;
    }

    props.reloadTeeTimeTabs();
    closeAddSqueezeTimeModal();
  }

  async function handleInitiateLottery(teetime: ITeeTimeTabNew, slot?: ITeeTimeSlotNew) {
    dispatch(initiateNewLotteryRequest());
    updateNewBookingModal({
      loading: true,
      loadingText: t("Loading tee time..."),
      slot: slot !== undefined ? slot : null,
      cartsAvailable: {
        nineHole: 0,
        eighteenHole: 0,
      },
    });

    const cartsAvailablePromise: Promise<IAPIResponse<IGetTeeTimeCartsAvailableResponse>> = facilityStore
      .teeSheetSettings?.validate_power_cart_count
      ? GetTeeTimeCartsAvailable(
          {
            tee_time_id: teetime.slots[0].tee_time_id,
          },
          false,
        )
      : Promise.resolve({ status: StatusCode.OK, data: null, message: null });

    const [teeTimeRes, lotteryTeeTimesRes, cartsAvailableRes] = await Promise.all([
      GetTeeTime(
        {
          id: teetime.slots[0].tee_time_id,
          extended: true,
          "extended-bookings": true,
          locks: true,
          turn_tee_time: true,
          active_restrictions: true,
        },
        false,
      ),
      GetTeeTime(
        {
          lottery_id: teetime?.slots[0]?.lottery_id,
          tee_sheet_id: teetime.slots[0]?.tee_sheet_id,
          date: teetime.slots[0]?.date,
        },
        false,
      ),
      cartsAvailablePromise,
    ]);

    if (teeTimeRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading tee time")));
      return;
    }

    if (lotteryTeeTimesRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting lottery times")));
      return;
    }

    let cartsAvailable9 = 0;
    let cartsAvailable18 = 0;

    if (facilityStore.teeSheetSettings?.validate_power_cart_count) {
      if (cartsAvailableRes.status !== StatusCode.OK) {
        dispatch(showError(t("Error loading tee time carts availability")));
        return;
      }

      cartsAvailable9 = cartsAvailableRes.data?.carts_available_9 ?? 0;
      cartsAvailable18 = cartsAvailableRes.data?.carts_available_18 ?? 0;
    }

    const updatedTeeTime = teeTimeRes.data[0];
    dispatch(selectTeeTimeSuccess(updatedTeeTime));

    if (updatedTeeTime.quantity_remaining === 0) {
      updateTeeTimeBookedPopup({ isOpen: true });
      return;
    }

    const filteredLotteryTimes = lotteryTeeTimesRes?.data?.filter(
      time => time?.division_id === teetime?.slots[0]?.division_id,
    );

    const lottery = state?.teeSheetLotteries?.find(lottery => lottery?.id === updatedTeeTime?.lottery_id);
    ReactDOM.unstable_batchedUpdates(() => {
      updateMainState({ selectedTeeTime: null });
      updateNewBookingModal({
        loading: false,
        loadingText: "",
        postingLock: false,
        lottery,
        lotteryTeeTimes: filteredLotteryTimes,
        cartsAvailable: {
          nineHole: cartsAvailable9,
          eighteenHole: cartsAvailable18,
        },
      });
    });
  }

  // BOOKING FUNCTIONS
  async function handleInitiateBooking(teetime: ITeeTimeTabNew, slot?: ITeeTimeSlotNew, groupBookingId?: string) {
    // Show booking modal
    dispatch(initiateNewBooking());

    // If tee time is not locked then load it
    updateNewBookingModal({
      loading: true,
      loadingText: t("Loading tee time..."),
      slot: slot !== undefined ? slot : null,
      cartsAvailable: {
        nineHole: 0,
        eighteenHole: 0,
      },
      groupBookingId,
    });

    const cartsAvailablePromise: Promise<IAPIResponse<IGetTeeTimeCartsAvailableResponse>> = facilityStore
      .teeSheetSettings?.validate_power_cart_count
      ? GetTeeTimeCartsAvailable(
          {
            tee_time_id: teetime.slots[0].tee_time_id,
          },
          false,
        )
      : Promise.resolve({ status: StatusCode.OK, data: null, message: null });

    const [teetimeRes, cartsAvailableRes] = await Promise.all([
      GetTeeTime(
        {
          id: teetime.slots[0].tee_time_id,
          extended: true,
          "extended-bookings": true,
          locks: true,
          turn_tee_time: true,
          active_restrictions: true,
        },
        false,
      ),
      cartsAvailablePromise,
    ]);

    if (teetimeRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading tee time")));
      return;
    }

    let cartsAvailable9 = 0;
    let cartsAvailable18 = 0;

    if (facilityStore.teeSheetSettings?.validate_power_cart_count) {
      if (cartsAvailableRes.status !== StatusCode.OK) {
        dispatch(showError(t("Error loading tee time carts availability")));
        return;
      }

      cartsAvailable9 = cartsAvailableRes.data?.carts_available_9 ?? 0;
      cartsAvailable18 = cartsAvailableRes.data?.carts_available_18 ?? 0;
    }

    const updatedTeeTime = teetimeRes.data[0];
    dispatch(selectTeeTimeSuccess(updatedTeeTime));

    if (updatedTeeTime.quantity_remaining === 0) {
      updateTeeTimeBookedPopup({ isOpen: true });
      return;
    }

    // Lock the tee time
    updateNewBookingModal({
      loadingText: t("Checking tee time availability..."),
      postingLock: true,
    });

    const postLockRes = await PostTeeTimeLock({ tee_time_id: updatedTeeTime.id }, false);

    // Tee time is locked by another user
    if (postLockRes.status !== StatusCode.OK) {
      updateTeeTimeLockedPopup({ isOpen: true });
      updateNewBookingModal({ loading: false, loadingText: "", postingLock: false, groupBookingId: null });
      return;
    }

    updateMainState({ selectedTeeTime: null });
    updateNewBookingModal({
      loading: false,
      loadingText: "",
      postingLock: false,
      cartsAvailable: {
        nineHole: cartsAvailable9,
        eighteenHole: cartsAvailable18,
      },
    });
  }

  async function handleEditBooking(teetime: ITeeTimeTabNew, bookingId: number) {
    // Show edit booking modal while data is loading
    dispatch(initiateEditBooking());

    // Load booking
    updateEditBookingModal({
      isOpen: true,
      loading: true,
      loadingText: t("Loading booking..."),
      cartsAvailable: {
        nineHole: 0,
        eighteenHole: 0,
      },
    });

    const bookingRes = await GetBooking({ id: bookingId }, false);

    //Check if selected booking has been cancelled by another user
    if (bookingRes.status !== StatusCode.OK || bookingRes.data[0]?.status === "cancelled") {
      props.reloadTeeTimeTabs();
      dispatch(showError(t("Booking has been cancelled by another user")));
      return;
    }

    const cartsAvailablePromise: Promise<IAPIResponse<IGetTeeTimeCartsAvailableResponse>> = facilityStore
      .teeSheetSettings?.validate_power_cart_count
      ? GetTeeTimeCartsAvailable(
          {
            tee_time_id: teetime.slots[0].tee_time_id,
          },
          false,
        )
      : Promise.resolve({ status: StatusCode.OK, data: null, message: null });

    const [selectTeeTimeAction, cartAvailableRes] = await Promise.all([
      dispatch(selectTeeTime(teetime.slots[0].tee_time_id, true, false)),
      cartsAvailablePromise,
    ]);

    if (selectTeeTimeAction.type === TeesheetActionTypes.UPDATE_SELECTED_TEETIME_ERROR_FACILITY) {
      dispatch(showError(t("Error loading tee time")));
      return;
    }

    let cartsAvailable9 = 0;
    let cartsAvailable18 = 0;

    if (facilityStore.teeSheetSettings?.validate_power_cart_count) {
      if (cartAvailableRes.status !== StatusCode.OK) {
        dispatch(showError(t("Error loading tee time carts availability")));
        return;
      }

      cartsAvailable9 = cartAvailableRes.data?.carts_available_9 ?? 0;
      cartsAvailable18 = cartAvailableRes.data?.carts_available_18 ?? 0;
    }

    updateEditBookingModal({
      loadingText: t("Checking tee time availability..."),
      postingLock: true,
    });

    const postLockRes = await PostTeeTimeLock({ tee_time_id: teetime.slots[0].tee_time_id }, false);

    if (postLockRes.status !== StatusCode.OK) {
      return;
    }

    // Tee time is locked by another user
    if (postLockRes.status !== StatusCode.OK) {
      updateEditBookingModal({
        selectedBooking: null,
        selectedTeeTime: null,
        loading: false,
        postingLock: false,
        teeTimeLock: false,
      });
      dispatch(showError(t("secure.facility.tee_sheet.tee_sheet_booking_details.005")));
    }

    updateEditBookingModal({
      selectedBooking: bookingRes.data[0],
      selectedTeeTime: teetime,
      cartsAvailable: {
        nineHole: cartsAvailable9,
        eighteenHole: cartsAvailable18,
      },
      loading: false,
      postingLock: false,
    });
  }

  async function cancelBooking() {
    const cancelRes = await PutCancelBooking(
      {
        tee_time_booking_id: Number(cancelBookingPopup.teetimeToCancelBookingId),
        send_cancellation_email: cancelBookingPopup.send_cancellation_email,
      },
      true,
    );

    if (cancelRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error cancelling booking")));
      return;
    }

    updateCancelBookingPopup({
      isOpen: false,
      teetimeToCancelDate: "",
      teetimeToCancelTime: "",
      teetimeToCancelBookingId: null,
    });

    dispatch(showSuccess(t("Successfully cancelled booking")));
    props.reloadTeeTimeTabs();
  }

  async function cancelBookingParticipant() {
    const cancelRes = await PutCancelParticipant({ id: cancelPlayerPopup.bookingParticipantId }, true);
    if (cancelRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error cancelling player")));
      return;
    }

    updateCancelPlayerPopup({
      isOpen: false,
      bookingParticipantName: "",
      bookingParticipantId: null,
    });

    dispatch(showSuccess(t("Successfully cancelled player")));
    props.reloadTeeTimeTabs();
  }

  async function handleNoShow(chargeNoShow: boolean) {
    if (!noShowModal.bookingParticipantIds || noShowModal?.bookingParticipantIds?.length === 0) {
      return;
    }

    dispatch(enqueue());
    const putBookingParticipantRes = await Promise.all(
      noShowModal?.bookingParticipantIds?.map(participantId =>
        PutBookingParticipantNoShow(
          {
            id: participantId,
            no_show_type: noShowModal?.type,
            variant_id: noShowChargeModal?.selectedProduct?.id,
            sendEmail: noShowModal?.sendNoShowChargeEmail,
          },
          false,
        ),
      ),
    );
    dispatch(dequeue());

    if (putBookingParticipantRes?.some(res => res?.status !== StatusCode.OK)) {
      const participantError = putBookingParticipantRes?.find(res => res?.status !== StatusCode.OK);
      dispatch(
        showError(
          typeof participantError?.data === "string"
            ? participantError?.data
            : t("Error marking participant as ") + valueToString(noShowModal.type),
        ),
      );
      return;
    }

    dispatch(showSuccess(t("Successfully marked participant as {{type}}", { type: valueToString(noShowModal.type) })));

    updateNoShowModal({
      isOpen: false,
      type: "charge",
      bookingParticipantIds: [],
      bookingId: null,
      chargeNoShow: false,
    });

    let updatedParticipants = putBookingParticipantRes?.map(res => res?.data);

    //Charge booking participants
    if (chargeNoShow) {
      const updatedParticipantsCharge = await handleNoShowCharge();
      if (updatedParticipantsCharge) {
        updatedParticipants = updatedParticipantsCharge;
      }
    }

    // Manually update the participants' no show values
    void updateParticipants(updatedParticipants);
  }

  function updateTeeTimesWithParticipants(teeTimes: ITeeTimeNew[], participants: Array<IBookingParticipant>) {
    const updatedTeeTimes = cloneDeep(teeTimes);

    participants?.forEach(participant => {
      updatedTeeTimes.forEach(teeTime => {
        const foundParticipantIndex = teeTime?.slots?.findIndex(slot => slot.booking_participant_id === participant.id);

        // Manually update the no show values of the matching participant
        if (foundParticipantIndex !== -1) {
          const participantToUpdate = teeTime.slots[foundParticipantIndex];

          teeTime.slots[foundParticipantIndex] = {
            ...participantToUpdate,
            check_in_status: participant?.check_in_status,
            no_show_type: participant?.no_show_type,
            order_financial_status: participant?.order_financial_status,
          };
        }
      });
    });

    return updatedTeeTimes;
  }

  function updateParticipants(updatedParticipants: Array<IBookingParticipant>) {
    if (noShowModal.teeTimeIndex != null) {
      const updatedTeeTimeTabs = updateTeeTimesWithParticipants(props.teetimeTabs, updatedParticipants);

      setTeeTimes(updatedTeeTimeTabs);
      void closeNoShowModal();
    } else {
      props.reloadTeeTimeTabs();
    }
  }

  async function handleNoShowCharge() {
    const manualCardPaymentOption = facilityStore?.paymentOptions?.find(
      paymentOption => paymentOption.payment_method === "manual_card",
    );

    if (
      !noShowChargeModal?.paymentMethod ||
      !noShowChargeModal?.selectedProduct ||
      noShowModal?.bookingParticipantIds?.length === 0 ||
      !manualCardPaymentOption
    ) {
      console.error("No show selection error");
      return null;
    }

    dispatch(enqueue());
    //Create order
    const postOrderRes = await PostOrder({ customer_id: noShowChargeModal?.booking?.customer_id }, false);
    if (postOrderRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating order")));
      dispatch(dequeue());
      return null;
    }

    //Create line item order
    const postLineItemOrderRes = await PostLineItemsToOrder(
      {
        order_id: postOrderRes.data.id,
        variant_id: noShowChargeModal?.selectedProduct?.id,
        quantity: noShowModal?.bookingParticipantIds?.length,
      },
      false,
    );

    if (postLineItemOrderRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error adding line items to order")));
      dispatch(dequeue());
      return null;
    }

    //Create transaction
    const postTransactionRes = await PostTransaction(
      {
        kind: "sale",
        amount: postLineItemOrderRes.data?.total_price,
        order_id: postOrderRes.data?.id,
        payment_option_id: manualCardPaymentOption?.id,
      },
      false,
    );

    if (postTransactionRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating transaction")));
      dispatch(dequeue());
      return null;
    }

    if (
      postTransactionRes.data.client_secret &&
      noShowChargeModal?.paymentMethod?.stripe_payment_method_id &&
      postTransactionRes.data.id &&
      postOrderRes.data.id
    ) {
      //Make the card payment
      const participantRes = await confirmCardPaymentNoShow(
        postTransactionRes.data.client_secret,
        String(noShowChargeModal?.paymentMethod?.stripe_payment_method_id),
        postTransactionRes.data.id,
        Number(postOrderRes.data.id),
      );
      if (participantRes) {
        const updatedParticipants: Array<IBookingParticipant> = participantRes?.map(
          res => res?.data,
        );
        if (noShowModal?.sendNoShowChargeEmail) {
          const noshowEmailRes = await PostNoShowEmail(
            {
              participant_ids: noShowModal?.bookingParticipantIds,
            },
            false,
          );
          if (noshowEmailRes.status !== StatusCode.OK) {
            dispatch(showError(t(noshowEmailRes.message ?? "Error sending no show charge email.")));
          }
        }
        dispatch(dequeue());
        return updatedParticipants;
      }
    } else {
      dispatch(showError(t("Error creating card payment")));
      dispatch(dequeue());
      return null;
    }

    dispatch(dequeue());
    return null;
  }

  // Confirm Stripe payment intent
  async function confirmCardPaymentNoShow(
    clientSecret: string,
    paymentMethodID: string,
    trans_id: number,
    orderId: number,
  ) {
    if (!stripe) {
      console.error("Stripe hook error");
      return null;
    }
    const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: paymentMethodID,
    });

    if (stripeError) {
      dispatch(showError(stripeError.message));
      return null;
    }

    //Capture the payment
    const captureRes = await PutCapture({ payment_intent_id: paymentIntent.id, transaction_id: trans_id }, false);

    if (captureRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error capturing payment")));
      return null;
    }

    //Update no show paid value and add order to the participants
    const participantRes = await Promise.all(
      noShowModal?.bookingParticipantIds?.map(participantId =>
        PutBookingParticipant({ id: participantId, no_show_paid: true, order_id: orderId }, false),
      ),
    );

    if (participantRes?.some(res => res?.status !== StatusCode.OK)) {
      dispatch(showError(t("Error updating a participant's no show status")));
    }

    dispatch(showSuccess(t("Successfully charged participants as no show")));
    void closeNoShowChargeModal();
    void closeNoShowModal();
    return participantRes;
  }

  function changeSlotBookingInformation(slot: ITeeTimeSlotNew, bookedSlot: ITeeTimeSlotNew = null) {
    // position and tee_time_slot_position can stay the same
    // quantity_remaining will be set at the very end of drag and drop for source and destination
    slot.tee_time_booking_id = bookedSlot?.tee_time_booking_id ?? null;
    slot.tee_time_slot_status = bookedSlot == null ? "available" : bookedSlot?.tee_time_slot_status ?? null;
    slot.tee_time_bookings_holes = bookedSlot?.tee_time_bookings_holes ?? null;
    slot.tee_time_bookings_source = bookedSlot?.tee_time_bookings_source ?? null;
    slot.tee_time_bookings_completed_at = bookedSlot?.tee_time_bookings_completed_at ?? null;
    slot.has_notes = bookedSlot?.has_notes ?? null;
    slot.power_cart_quantity = bookedSlot?.power_cart_quantity ?? null;
    slot.booking_participant_id = bookedSlot?.booking_participant_id ?? null;
    slot.customer_id = bookedSlot?.customer_id ?? null;
    slot.guest = bookedSlot?.guest ?? null;
    slot.tee_time_booking_participant_status = bookedSlot?.tee_time_booking_participant_status ?? null;
    slot.check_in_status = bookedSlot?.check_in_status ?? null;
    slot.turn = bookedSlot?.turn ?? null;
    slot.customer_first_name = bookedSlot?.customer_first_name ?? null;
    slot.customer_last_name = bookedSlot?.customer_last_name ?? null;
    slot.customer_type_title = bookedSlot?.customer_type_title ?? null;
    slot.customer_type_subtitle = bookedSlot?.customer_type_subtitle ?? null;
    slot.customer_type_color = bookedSlot?.customer_type_color ?? null;
    slot.order_financial_status = bookedSlot?.order_financial_status ?? null;
    slot.no_show_type = bookedSlot?.no_show_type ?? null;
    slot.bag_number = bookedSlot?.bag_number ?? null;
  }

  function clearSlotWrapPreDragBoxes() {
    document.querySelectorAll(".tee-time-slots_container--pre-drag").forEach(node => {
      if (!(node instanceof HTMLElement)) {
        return;
      }
      node.classList.remove("tee-time-slots_container--pre-drag");
      node.style.removeProperty("--pre-drag-width");
      node.style.removeProperty("--pre-drag-height");
    });
  }

  function handleOnBeforeCapture(before: BeforeCapture) {
    const el = document.querySelector(
      `[data-rbd-draggable-id="${before.draggableId}"]`,
    );
    if (!(el instanceof HTMLElement)) {
      return;
    }
    if (!el.classList.contains("tee-time-slots_container-grouped")) {
      return;
    }
    if (!el.closest(".tee-sheet-tabs-division--slot-wrap")) {
      return;
    }

    const children = Array.from(el.children).filter(
      (child): child is HTMLElement => child instanceof HTMLElement,
    );
    if (children.length === 0) {
      return;
    }

    // Measure while display:contents so children still sit in the shared 2-col parent grid
    const rects = children.map(child => child.getBoundingClientRect());
    const left = Math.min(...rects.map(rect => rect.left));
    const top = Math.min(...rects.map(rect => rect.top));
    const right = Math.max(...rects.map(rect => rect.right));
    const bottom = Math.max(...rects.map(rect => rect.bottom));
    el.style.setProperty("--pre-drag-width", `${right - left}px`);
    el.style.setProperty("--pre-drag-height", `${bottom - top}px`);
    el.classList.add("tee-time-slots_container--pre-drag");
    // Force layout so rbd's dimension capture sees a real box
    void el.offsetWidth;
  }

  function handleOnDragStart(initial: DragStart) {
    if (teeSheetView === "side-by-side" && initial.source.droppableId === "side-by-side-division-headers") {
      setDragAndDropState(prev => ({
        ...prev,
        teetimeToolTipDisabled: true,
        foundSourceTeeTime: null,
        draggingSlotGroup: null,
      }));
      return;
    }

    let foundSourceTeeTime: ITeeTimeDraggable = null;

    if (teeSheetView === "side-by-side") {
      for (const division of groupedSideBySideDivisions) {
        foundSourceTeeTime = division.groupedTeeTimes.find(
          teeTime => teeTime.slots[0][0].tee_time_id === Number(initial.source.droppableId),
        );

        if (foundSourceTeeTime) {
          break;
        }
      }
    } else {
      foundSourceTeeTime = state.groupedTeeTimes.find(
        teeTime => teeTime.slots[0][0].tee_time_id === Number(initial.source.droppableId),
      );
    }

    if (!foundSourceTeeTime) {
      return;
    }

    const draggingSlotGroup = foundSourceTeeTime.slots?.find(slotGroup => {
      return slotGroup?.[0]?.tee_time_slot_id === Number(initial.draggableId);
    });
    const draggingSlotGroupCopy = draggingSlotGroup ? cloneDeep(draggingSlotGroup) : null;

    if (draggingSlotGroupCopy) {
      bookingDragRef.current = {
        sourceTeeTime: foundSourceTeeTime,
        draggingSlotGroup: draggingSlotGroupCopy,
      };
      window.removeEventListener("pointermove", handleDateBarPointerMove);
      window.addEventListener("pointermove", handleDateBarPointerMove);
      props.onBookingDragActiveChange?.(true);
    }

    setDragAndDropState(prev => ({
      ...prev,
      teetimeToolTipDisabled: true,
      draggingSlotGroup: draggingSlotGroupCopy,
      foundSourceTeeTime,
    }));
  }

  async function dropBookingOnDate(
    dropDate: string,
    bookingDrag: { sourceTeeTime: ITeeTimeDraggable; draggingSlotGroup: ITeeTimeSlotNew[] },
  ) {
    const { sourceTeeTime, draggingSlotGroup } = bookingDrag;
    const sourceSlot = draggingSlotGroup[0];

    if (
      !sourceSlot ||
      draggingSlotGroup.some(
        slot =>
          slot.tee_time_booking_id == null || slot.blocked_type === "crossover" || slot.check_in_status === "checked",
      ) ||
      loadingTeetimeTabActionsContainsId(loadingTeetimeTabActions, sourceSlot.tee_time_id)
    ) {
      return;
    }

    let moveBookingLength = 0;
    sourceTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        if (slot.tee_time_booking_id === sourceSlot.tee_time_booking_id) {
          moveBookingLength++;
        }
      });
    });

    const requestId = ++dateDropRequestRef.current;
    const teeTimesRes = await GetTeeTime(
      {
        date: dropDate,
        course_id: sourceSlot.course_id,
        division_id: sourceSlot.division_id,
        start_hole: sourceSlot.start_hole || undefined,
        start_time: sourceSlot.start_time,
        end_time: sourceSlot.start_time,
      },
      false,
    );

    if (requestId !== dateDropRequestRef.current) {
      return;
    }

    if (teeTimesRes.status !== StatusCode.OK || !Array.isArray(teeTimesRes.data)) {
      dispatch(showError(t("Error loading tee time")));
      return;
    }

    const destination = teeTimesRes.data.find(
      teeTime =>
        sameStartTime(teeTime.start_time, sourceSlot.start_time) &&
        String(teeTime.start_hole) === String(sourceSlot.start_hole),
    );
    if (!destination) {
      dispatch(showError(t("Bookings can't be moved into this tee time")));
      return;
    }

    const holes = sourceSlot.tee_time_bookings_holes;
    const holeMove = destinationHoleMove(destination, holes, moveBookingLength);
    if (holeMove === "wrong-holes") {
      dispatch(showError(`${t("The tee time does not allow bookings for")} ${holes} ${t("holes")}`));
      return;
    }
    if (holeMove === "unavailable") {
      dispatch(showError(t("Bookings can't be moved into this tee time")));
      return;
    }

    updateMoveTeeTimeBookingConfirmationPopup({
      isOpen: true,
      teeTimeBookingID: sourceSlot.tee_time_booking_id,
      startTeeTimeID: sourceSlot.tee_time_id,
      destinationTeeTimeID: destination.id,
      displayHoleConversionWarning: holeMove === "convert",
      draggingBookingHoles: holes,
    });
  }

  function handleOnDragEnd(result: DropResult) {
    window.removeEventListener("pointermove", handleDateBarPointerMove);
    const dropDate = dateBarDropDateRef.current;
    const bookingDrag = bookingDragRef.current;
    dateBarDropDateRef.current = null;
    bookingDragRef.current = null;
    props.onBookingDragActiveChange?.(false);
    clearDateBarDropTarget();
    clearSlotWrapPreDragBoxes();
    setDragAndDropState(prev => ({ ...prev, teetimeToolTipDisabled: false }));

    if (dropDate && bookingDrag) {
      setDragAndDropState(prev => ({
        ...prev,
        draggingSlotGroup: undefined,
        foundSourceTeeTime: undefined,
      }));
      void dropBookingOnDate(dropDate, bookingDrag);
      return;
    }

    if (teeSheetView === "side-by-side" && result.source.droppableId === "side-by-side-division-headers") {
      if (result.destination != null && props.sideBySideSettingsKeyPrefix != null) {
        const from = result.source.index;
        const to = result.destination.index;
        if (from !== to) {
          const nextIds = [...props.sideBySideSettings.selectedDivisionIds];
          const [removed] = nextIds.splice(from, 1);
          nextIds.splice(to, 0, removed);
          props.updateSideBySideSettings(props.sideBySideSettingsKeyPrefix, {
            ...props.sideBySideSettings,
            selectedDivisionIds: nextIds,
          });
        }
      }
      return;
    }

    if (
      !result.destination ||
      result.source.droppableId === result.destination.droppableId ||
      dragAndDropState.foundSourceTeeTime == null ||
      dragAndDropState.draggingSlotGroup == null ||
      dragAndDropState.draggingSlotGroup.some(
        slot =>
          slot.tee_time_booking_id == null || slot.blocked_type === "crossover" || slot.check_in_status === "checked",
      ) ||
      loadingTeetimeTabActionsContainsId(loadingTeetimeTabActions, dragAndDropState.draggingSlotGroup[0].tee_time_id)
    ) {
      return;
    }

    let foundDestTeeTime: ITeeTimeDraggable = null;

    if (teeSheetView === "side-by-side") {
      for (const division of groupedSideBySideDivisions) {
        foundDestTeeTime = division.groupedTeeTimes.find(
          teeTime => teeTime.slots[0][0].tee_time_id === Number(result.destination.droppableId),
        );

        if (foundDestTeeTime) {
          break;
        }
      }
    } else {
      foundDestTeeTime = state.groupedTeeTimes.find(
        teeTime => teeTime.slots[0][0].tee_time_id === Number(result.destination.droppableId),
      );
    }

    if (!foundDestTeeTime) {
      return;
    }

    const sourceTeeTime = cloneDeep(dragAndDropState.foundSourceTeeTime);
    const destTeeTime = cloneDeep(foundDestTeeTime);

    if (loadingTeetimeTabActionsContainsId(loadingTeetimeTabActions, destTeeTime.slots[0][0].tee_time_id)) {
      return;
    }

    let draggingToCrossover = false;

    destTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        if (slot.blocked_type === "crossover") {
          draggingToCrossover = true;
        }
      });
    });

    if (draggingToCrossover) {
      return;
    }

    let moveBookingLength = 0;

    sourceTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        if (slot.tee_time_booking_id === dragAndDropState.draggingSlotGroup[0].tee_time_booking_id) {
          moveBookingLength++;
        }
      });
    });

    const freeSlots: ITeeTimeSlotNew[] = [];

    destTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        if (slot.tee_time_slot_status === "available") {
          freeSlots.push(slot);
        }
      });
    });

    if (moveBookingLength > freeSlots.length) {
      return;
    }

    const moveSlots = take(freeSlots, moveBookingLength);

    let destDraggingSlotToCopyIndex = 0;

    destTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        if (moveSlots.some(moveSlot => moveSlot.tee_time_slot_id === slot.tee_time_slot_id)) {
          changeSlotBookingInformation(slot, dragAndDropState.draggingSlotGroup?.[destDraggingSlotToCopyIndex]);
          destDraggingSlotToCopyIndex++;
        }
      });
    });

    const updatedDestSlots: TDraggableSlot = [];

    destTeeTime.slots.forEach(slotGroup => {
      if (
        slotGroup.length === 1 &&
        slotGroup[0].tee_time_booking_id === dragAndDropState.draggingSlotGroup[0].tee_time_booking_id &&
        updatedDestSlots?.[updatedDestSlots.length - 1]?.[0]?.tee_time_booking_id ===
          dragAndDropState.draggingSlotGroup[0].tee_time_booking_id
      ) {
        updatedDestSlots[updatedDestSlots.length - 1].push(slotGroup[0]);
      } else {
        updatedDestSlots.push(slotGroup);
      }
    });

    destTeeTime.slots = updatedDestSlots;

    const updatedSourceSlots: TDraggableSlot = [];
    const shiftIndexes: number[] = [];

    sourceTeeTime.slots.forEach(slotGroup => {
      if (shiftIndexes.length > 0) {
        if (slotGroup.length === 1 && slotGroup[0].tee_time_slot_status === "available") {
          updatedSourceSlots.push(slotGroup);
          shiftIndexes.push(updatedSourceSlots.length - 1);
        } else if (slotGroup.every(slot => slot.tee_time_slot_status === "booked")) {
          const updatedSlotGroup = cloneDeep(slotGroup);

          const originalUpdateSlotGroup = cloneDeep(updatedSlotGroup);
          const originalSlotGroupLength = updatedSlotGroup.length;

          updatedSlotGroup.forEach(slot => {
            changeSlotBookingInformation(slot);
          });

          const availableSlots = chunk(updatedSlotGroup, 1);

          updatedSourceSlots.push(...availableSlots);
          shiftIndexes.push(...range(updatedSourceSlots.length - availableSlots.length, updatedSourceSlots.length));

          const slotIndexesToGroupAndBook = take(shiftIndexes, originalSlotGroupLength);

          const newSlotGroup: ITeeTimeSlotNew[] = [];

          let sourceDraggingSlotToCopyIndex = 0;

          for (const slotIndex of slotIndexesToGroupAndBook) {
            const slot = cloneDeep(updatedSourceSlots[slotIndex][0]);
            changeSlotBookingInformation(slot, originalUpdateSlotGroup?.[sourceDraggingSlotToCopyIndex]);

            newSlotGroup.push(slot);
            sourceDraggingSlotToCopyIndex++;
          }

          updatedSourceSlots.splice(slotIndexesToGroupAndBook[0], slotIndexesToGroupAndBook.length, newSlotGroup);

          shiftIndexes.splice(0, originalSlotGroupLength);
        }
      } else if (
        slotGroup.some(slot => slot.tee_time_booking_id === dragAndDropState.draggingSlotGroup[0].tee_time_booking_id)
      ) {
        const updatedSlotGroup = cloneDeep(slotGroup);

        updatedSlotGroup.forEach(slot => {
          changeSlotBookingInformation(slot);
        });

        const availableSlots = chunk(updatedSlotGroup, 1);

        updatedSourceSlots.push(...availableSlots);
        shiftIndexes.push(...range(updatedSourceSlots.length - availableSlots.length, updatedSourceSlots.length));
      } else {
        updatedSourceSlots.push(slotGroup);
      }
    });

    sourceTeeTime.slots = updatedSourceSlots;

    let sourceQuantityRemaining = 4;
    let destQuantityRemaining = 4;

    sourceTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        if (slot.tee_time_slot_status === "booked") {
          sourceQuantityRemaining--;
        }
      });
    });

    destTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        if (slot.tee_time_slot_status === "booked") {
          destQuantityRemaining--;
        }
      });
    });

    sourceTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        slot.quantity_remaining = sourceQuantityRemaining;
      });
    });

    destTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        slot.quantity_remaining = destQuantityRemaining;
      });
    });

    const firstDestSlot = destTeeTime.slots[0][0];
    const destDivisions: number[] = JSON.parse(firstDestSlot.divisions_available);
    const draggingBookingHoles: number = dragAndDropState.draggingSlotGroup[0].tee_time_bookings_holes;

    if (!destDivisions.some(destDivision => destDivision === draggingBookingHoles / 9)) {
      dispatch(showError(`${t("The tee time does not allow bookings for")} ${draggingBookingHoles} ${t("holes")}`));

      return;
    }

    let nineHoleAvailable = false;
    let eighteenHoleAvailable = false;

    destDivisions?.forEach(division => {
      if (division === 1) {
        nineHoleAvailable = true;
      } else if (division === 2) {
        const leagueId = firstDestSlot.league_id;
        const tournamentId = firstDestSlot.tournament_id;

        if (
          (destDivisions?.length > 1 && firstDestSlot.turn_tee_time_id == null) ||
          isTurnBlockedForEighteenHoles(firstDestSlot?.turn_tee_time_blocked_type, firstDestSlot?.blocked_type) ||
          ((firstDestSlot?.turn_tee_time_league_id || firstDestSlot?.turn_tee_time_tournament_id) &&
            (leagueId !== firstDestSlot?.turn_tee_time_league_id ||
              tournamentId !== firstDestSlot?.turn_tee_time_tournament_id))
        ) {
          nineHoleAvailable = true;
        } else if (firstDestSlot.turn_tee_time_id != null) {
          if (firstDestSlot?.turn_tee_time_quantity_remaining >= moveBookingLength) {
            eighteenHoleAvailable = true;
          } else {
            nineHoleAvailable = true;
          }
        }
      }
    });

    let displayHoleConversionWarning = false;

    if (!nineHoleAvailable && !eighteenHoleAvailable) {
      dispatch(showError(t("Bookings can't be moved into this tee time")));
      return;
    } else if (
      (draggingBookingHoles === 9 && !nineHoleAvailable) ||
      (draggingBookingHoles === 18 && !eighteenHoleAvailable)
    ) {
      displayHoleConversionWarning = true;
    }

    updateMoveTeeTimeBookingConfirmationPopup({
      isOpen: true,
      teeTimeBookingID: dragAndDropState.draggingSlotGroup[0].tee_time_booking_id,
      startTeeTimeID: dragAndDropState.draggingSlotGroup[0].tee_time_id,
      destinationTeeTimeID: firstDestSlot.tee_time_id,
      displayHoleConversionWarning,
      draggingBookingHoles,
    });

    setDragAndDropState(prev => ({
      ...prev,
      draggingSlotGroup: undefined,
      foundSourceTeeTime: undefined,
      moveGroups: [...prev.moveGroups, { sourceTeeTime, destTeeTime }],
    }));
  }

  async function startMoveBookingProcess() {
    const startId = moveTeeTimeBookingConfirmation.startTeeTimeID;
    const destId = moveTeeTimeBookingConfirmation.destinationTeeTimeID;
    const bookingId = moveTeeTimeBookingConfirmation.teeTimeBookingID;

    closeMoveTeeTimeBookingConfirmationPopup();

    await moveBooking(
      {
        type: "move-bookings",
        ids: [startId, destId],
      },
      bookingId,
    );
  }

  function handleCancelTeeTimeMove() {
    const startId = moveTeeTimeBookingConfirmation.startTeeTimeID;
    const destId = moveTeeTimeBookingConfirmation.destinationTeeTimeID;

    setDragAndDropState(prev => {
      return {
        ...prev,
        moveGroups: prev.moveGroups.filter(
          moveGroup =>
            moveGroup.sourceTeeTime?.slots?.[0]?.[0]?.tee_time_id !== startId &&
            moveGroup.destTeeTime?.slots?.[0]?.[0]?.tee_time_id !== destId,
        ),
      };
    });

    closeMoveTeeTimeBookingConfirmationPopup();
  }

  async function moveBooking(moveLoadingTeetimeTabAction: TLoadingMoveBookingsAction, bookingId: number) {
    try {
      setLoadingTeetimeTabActions(prev => {
        return [...prev, moveLoadingTeetimeTabAction];
      });

      const putMove = await PutMoveBooking(
        {
          booking_id: bookingId,
          tee_time_id: moveLoadingTeetimeTabAction.ids[1],
        },
        false,
        teeSheetTabsExitCancelSource.current.token,
      );

      if (teeSheetTabsExitCancelSource.current.token?.reason) {
        return;
      }

      if (putMove.status !== StatusCode.OK) {
        dispatch(showError(typeof putMove.data === "string" ? putMove.data : t("Error moving booking")));

        return;
      }

      const loadTeeTimeTabsStatus = await props.reloadActionTeeTimeTabs(moveLoadingTeetimeTabAction, false);

      if (loadTeeTimeTabsStatus === "failure") {
        return;
      }

      if (moveTeeTimeBookingConfirmation.sendConfirmation) {
        const putBookingConfirmationResponse = await PutBookingConfirmation(
          { booking_group_id: putMove.data.booking_group_id },
          false,
          teeSheetTabsExitCancelSource.current.token,
        );

        if (teeSheetTabsExitCancelSource.current.token?.reason) {
          return;
        }

        if (putBookingConfirmationResponse.status !== StatusCode.OK) {
          dispatch(showError(t("Failed to send booking confirmation")));
          return;
        }
      }

      dispatch(showSuccess(t("Booking moved successfully")));
    } finally {
      setLoadingTeetimeTabActions(prev =>
        prev.filter(
          prevLoadingTeetimeTabAction =>
            !loadingTeetimeTabActionsAreEqual(prevLoadingTeetimeTabAction, moveLoadingTeetimeTabAction),
        ),
      );

      setDragAndDropState(prev => {
        return {
          ...prev,
          moveGroups: prev.moveGroups.filter(
            moveGroup =>
              moveGroup.sourceTeeTime?.slots?.[0]?.[0]?.tee_time_id !== moveLoadingTeetimeTabAction.ids[0] &&
              moveGroup.destTeeTime?.slots?.[0]?.[0]?.tee_time_id !== moveLoadingTeetimeTabAction.ids[1],
          ),
        };
      });
    }
  }

  // EVENT FUNCTIONS
  async function loadTeeSheetTournaments(ids: Array<number>, token?: CancelToken) {
    if (ids?.length > 0) {
      const tournamentRes = await GetTournament({ ids: ids }, false, token);

      if (token && token.reason) {
        return;
      }

      updateMainState({ teeSheetTournaments: tournamentRes.status === StatusCode.OK ? tournamentRes.data : [] });
    } else {
      updateMainState({ teeSheetTournaments: [] });
    }
  }

  async function loadTeeSheetLeagues(ids: Array<number>, token?: CancelToken) {
    if (ids?.length > 0) {
      const leagueRes = await GetLeague({ ids: ids }, false, token);

      if (token && token.reason) {
        return;
      }

      updateMainState({ teeSheetLeagues: Number(leagueRes.status) == Number(StatusCode.OK) ? leagueRes.data : [] });
    } else {
      updateMainState({ teeSheetLeagues: [] });
    }
  }

  async function loadTeeSheetLotteries(ids: Array<number>, token?: CancelToken) {
    if (ids?.length > 0) {
      const lotteryRes = await GetTeeTimeLottery({ ids: ids }, false, token);

      if (token && token.reason) {
        return;
      }

      updateMainState({ teeSheetLotteries: Number(lotteryRes.status) == Number(StatusCode.OK) ? lotteryRes.data : [] });
    } else {
      updateMainState({ teeSheetLotteries: [] });
    }
  }

  async function removeShotgunTemplate() {
    const params = {
      tee_sheet_id: removeShotgunPopup.teetime.slots[0].tee_sheet_id,
      tournament_id: removeShotgunPopup.teetime.slots[0].tournament_id
        ? removeShotgunPopup.teetime.slots[0].tournament_id
        : null,
      league_id: removeShotgunPopup.teetime.slots[0].league_id ? removeShotgunPopup.teetime.slots[0].league_id : null,
      shotgun_application_id: removeShotgunPopup.teetime.slots[0].shotgun_application_id,
    };

    const removeRes = await RemoveShotgunTemplate(params, true);

    if (Number(removeRes.status) !== Number(StatusCode.OK)) {
      dispatch(showError(t("Error removing shotgun template")));
      return;
    }

    dispatch(showSuccess(t("Successfully removed shotgun template")));

    closeRemoveShotgunPopup();

    props.reloadTeeTimeTabs();
  }

  async function importDrawsheet(selectedFile: File, customer_id: number) {
    if (!customer_id) {
      dispatch(showError(t("No customer selected")));
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("shotgun_application_id", String(fileImportModal.shotgun_application_id));
    formData.append("customer_id", String(customer_id));

    const importRes = await ImportDrawsheet(formData, true);

    if (importRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error importing draw sheet")));
      closeFileImportModal();
      return;
    }

    dispatch(showSuccess(t("Draw sheet imported successfully")));
    closeFileImportModal();
    props.reloadTeeTimeTabs();
  }

  async function importPlayersFile(selectedFile: File, customer_id: number, holes: number) {
    if (!customer_id) {
      dispatch(showError(t("No customer selected")));
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("date", String(importPlayersFileModal.date));
    formData.append("tee_sheet_id", String(importPlayersFileModal.tee_sheet_id));
    formData.append("start_time", String(importPlayersFileModal.start_time));
    formData.append("end_time", String(importPlayersFileModal.end_time));
    formData.append("customer_id", String(customer_id));
    formData.append("holes", String(holes));
    if (importPlayersFileModal?.tournament_id) {
      formData.append("tournament_id", String(importPlayersFileModal.tournament_id));
    } else if (importPlayersFileModal?.league_id) {
      formData.append("league_id", String(importPlayersFileModal.league_id));
    }

    const importRes = await ImportPlayersFile(formData, true);

    if (importRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error importing players")));
      void closeImportPlayersFileModal();
      return;
    }

    dispatch(showSuccess(t("Successfully imported players")));
    void closeImportPlayersFileModal();
    void props?.reloadTeeTimeTabs();
  }

  function handleCheckboxChange(event: any) {
    const { id, checked } = event.target;
    updateCancelBookingPopup({
      [id]: checked,
    });
  }

  function handleNoShowCheckboxChange(participantId: number) {
    let updatedSelectedParticipantIds: Array<number> =
      noShowModal?.bookingParticipantIds == null ? [] : [...noShowModal.bookingParticipantIds];

    const participantIndex = updatedSelectedParticipantIds.indexOf(participantId);
    if (participantIndex === -1) {
      updatedSelectedParticipantIds = [...updatedSelectedParticipantIds, participantId];
    } else {
      updatedSelectedParticipantIds = updatedSelectedParticipantIds?.filter(id => id !== participantId);
    }
    updateNoShowModal({ bookingParticipantIds: updatedSelectedParticipantIds });
  }

  async function getNoShowBooking() {
    const noShowCancelToken = cancelToken();
    //Show loader
    updateNoShowChargeModal({ isOpen: true, booking: undefined });
    if (!noShowModal?.bookingId) {
      dispatch(showError(t("Booking not found")));
      void closeNoShowChargeModal();
      return;
    }

    const bookingRes = await GetBooking({ id: noShowModal?.bookingId }, false, noShowCancelToken);
    if (bookingRes?.status !== StatusCode.OK) {
      void closeNoShowChargeModal();
      if (noShowCancelToken?.reason) {
        return;
      }

      dispatch(showError(t("Error getting booking")));
      return;
    }
    if (bookingRes?.data?.length > 0) {
      const booking = bookingRes?.data[0];
      if (!booking?.customer_payment_method_id) {
        void closeNoShowChargeModal();
        updateNoCreditCardPopup({ isOpen: true });
        return;
      }
      const noShowProducts = await GetProduct({ type: "No Show Green Fee", extended: true }, false, noShowCancelToken);
      if (noShowProducts?.status !== StatusCode.OK) {
        void closeNoShowChargeModal();
        if (noShowCancelToken?.reason) {
          return;
        }
        dispatch(showError(t("Error getting No Show Products")));
        return;
      }
      //Get the customer payment method information
      void getCustomerPaymentMethod(Number(booking?.customer_payment_method_id), Number(booking?.customer_id));
      updateNoShowChargeModal({ isOpen: true, booking: booking, noShowProducts: noShowProducts?.data });
    } else {
      dispatch(showError(t("Booking not found")));
      void closeNoShowChargeModal();
      return;
    }
  }

  async function getCustomerPaymentMethod(paymentMethodId: number, customerId: number) {
    const paymentMethodRes = await GetCustomerPaymentMethod({ id: paymentMethodId, customer_id: customerId }, false);

    if (paymentMethodRes?.status !== StatusCode.OK) {
      void closeNoShowChargeModal();
      dispatch(showError(t("Error getting payment method on the booking")));
      return;
    }

    updateNoShowChargeModal({ paymentMethod: paymentMethodRes?.data[0] });
  }

  async function createLottery() {
    if (lotteryModal?.openDate > lotteryModal?.closeDate) {
      dispatch(showError(t("Open date cannot be past the close date")));
      return;
    }

    if (lotteryModal?.startTime > lotteryModal?.endTime) {
      dispatch(showError(t("Start time cannot be past the end time")));
      return;
    }

    const res = await PostTeeTimeLottery(
      {
        course_id: lotteryModal?.courseId,
        date: lotteryModal?.date,
        open_date: formatDate(lotteryModal?.openDate),
        close_date: formatDate(lotteryModal?.closeDate),
        start_time: lotteryModal?.startTime,
        end_time: lotteryModal?.endTime,
        number_of_time_preferences: lotteryModal.timePreferences,
        allow_time_preference_window: lotteryModal.allowTimePreferenceWindow,
        allow_guests: lotteryModal.allowGuests,
      },
      true,
    );

    if (res?.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating lottery")));
      return;
    }
    dispatch(showSuccess(t("Successfully created lottery")));
    void closeLotteryModal();
    //Reload tee times
    void props?.reloadTeeTimeTabs();
  }

  async function updateLottery() {
    if (editLotteryModal?.openDate > editLotteryModal?.closeDate) {
      dispatch(showError(t("Open date cannot be past the close date")));
      return;
    }

    const res = await PutTeeTimeLottery(
      {
        id: editLotteryModal?.lotteryId,
        // tee_sheet_id: teetime.slots[0]?.tee_sheet_id,
        open_date: formatDate(editLotteryModal?.openDate),
        close_date: formatDate(editLotteryModal?.closeDate),
        number_of_time_preferences: editLotteryModal.timePreferences,
        allow_time_preference_window: editLotteryModal.allowTimePreferenceWindow,
        allow_guests: editLotteryModal.allowGuests,
      },
      true,
    );

    if (res?.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating lottery")));
      return;
    }
    dispatch(showSuccess(t("Successfully updated lottery")));
    void closeEditLotteryModal();
    //Reload tee times
    void props?.reloadTeeTimeTabs();
  }

  const renderTeeSheetTabsContainer = () => {
    const spinner = (
      <div style={{ height: "24px" }}>
        <Spin />
      </div>
    );

    const errorCallout = <Callout type="error" title={t("No Tee Times Found")} />;

    if (teeSheetView === "side-by-side") {
      if (!groupedSideBySideDivisions) {
        return spinner;
      } else if (groupedSideBySideDivisions.length === 0) {
        return errorCallout;
      } else {
        return (
          <DragDropContext
            onBeforeCapture={handleOnBeforeCapture}
            onDragStart={handleOnDragStart}
            onDragEnd={handleOnDragEnd}
          >
            <div
              className="tee-sheet-tabs-side-by-side-divisions"
              style={{
                gridTemplateColumns: `repeat(${groupedSideBySideDivisions.length}, 1fr)`,
              }}
            >
              <Droppable droppableId="side-by-side-division-headers" direction="horizontal">
                {droppableProvided => (
                  <div
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                    style={{
                      display: "flex",
                      gridColumn: "1 / -1",
                      gridRow: 1,
                      gap: 15,
                      width: "100%",
                    }}
                  >
                    {groupedSideBySideDivisions.map((groupedSideBySideDivision, headerIndex) => {
                      return (
                        <Draggable
                          key={groupedSideBySideDivision.id}
                          draggableId={`division-header-${groupedSideBySideDivision.id}`}
                          index={headerIndex}
                        >
                          {draggableProvided => (
                            <div
                              ref={draggableProvided.innerRef}
                              {...draggableProvided.draggableProps}
                              {...draggableProvided.dragHandleProps}
                              className="tee-sheet-tabs-side-by-side-division-header"
                              style={{
                                ...draggableProvided.draggableProps.style,
                                flex: 1,
                                minWidth: 0,
                                ...calculateDivisionColors(groupedSideBySideDivision.division?.color),
                              }}
                            >
                              {groupedSideBySideDivision.title}
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {droppableProvided.placeholder}
                  </div>
                )}
              </Droppable>
              {groupedSideBySideDivisions.map(groupedSideBySideDivision => {
                return (
                  <NewTeeSheetTabsDivision
                    key={groupedSideBySideDivision.id}
                    divisionClassName="tee-sheet-tabs-side-by-side-division"
                    teeSheetView={teeSheetView}
                    sortedTeeTimes={groupedSideBySideDivision.sortedTeeTimes}
                    groupedTeeTimes={groupedSideBySideDivision.groupedTeeTimes}
                    teetimeBookingCategories={props.teetimeBookingCategories}
                    dragAndDropState={dragAndDropState}
                    loadingTeetimeTabActions={loadingTeetimeTabActions}
                    teeSheetLeagues={state.teeSheetLeagues}
                    teeSheetTournaments={state.teeSheetTournaments}
                    teeSheetLotteries={state?.teeSheetLotteries}
                    allDivisions={allDivisions}
                    handleInitiateBooking={handleInitiateBooking}
                    handleEditBooking={handleEditBooking}
                    updateModalState={updateModalState}
                    updateMainState={updateMainState}
                    handleSelectDivisions={handleSelectDivisions}
                    handleInitiateLottery={handleInitiateLottery}
                    lotteryBuilds={lotteryBuild}
                    jumpToTime={props.jumpToTime}
                    navigationKey={`${props.autoScrollNavigationKeyPrefix}|${groupedSideBySideDivision.id}`}
                    preferredDivisionId={groupedSideBySideDivision.id}
                    reloadTeeTimeTabs={props.reloadTeeTimeTabs}
                    duplicateBookingModal={duplicateBookingModal}
                    updateDuplicateBookingModal={updateDuplicateBookingModal}
                    registerSideBySideScrollContainer={
                      teeSheetView === "side-by-side" && props.sideBySideSettings.syncDivisionScroll
                        ? registerSideBySideScrollContainer
                        : undefined
                    }
                    onSideBySideDivisionScroll={
                      teeSheetView === "side-by-side" && props.sideBySideSettings.syncDivisionScroll
                        ? onSideBySideDivisionScroll
                        : undefined
                    }
                    sideBySideScrollDivisionId={
                      teeSheetView === "side-by-side" && props.sideBySideSettings.syncDivisionScroll
                        ? groupedSideBySideDivision.id
                        : undefined
                    }
                    sideBySideSettings={props.sideBySideSettings}
                  />
                );
              })}
            </div>
          </DragDropContext>
        );
      }
    } else {
      if (!state.groupedTeeTimes) {
        return spinner;
      } else if (state.groupedTeeTimes.length === 0) {
        return errorCallout;
      } else {
        return (
          <DragDropContext
            onBeforeCapture={handleOnBeforeCapture}
            onDragStart={handleOnDragStart}
            onDragEnd={handleOnDragEnd}
          >
            <NewTeeSheetTabsDivision
              teeSheetView={teeSheetView}
              sortedTeeTimes={state.sortedTeeTimes}
              groupedTeeTimes={state.groupedTeeTimes}
              teetimeBookingCategories={props.teetimeBookingCategories}
              dragAndDropState={dragAndDropState}
              loadingTeetimeTabActions={loadingTeetimeTabActions}
              teeSheetLeagues={state.teeSheetLeagues}
              teeSheetTournaments={state.teeSheetTournaments}
              teeSheetLotteries={state?.teeSheetLotteries}
              allDivisions={allDivisions}
              handleInitiateBooking={handleInitiateBooking}
              handleEditBooking={handleEditBooking}
              updateModalState={updateModalState}
              updateMainState={updateMainState}
              handleSelectDivisions={handleSelectDivisions}
              handleInitiateLottery={handleInitiateLottery}
              lotteryBuilds={lotteryBuild}
              jumpToTime={props.jumpToTime}
              navigationKey={`${props.autoScrollNavigationKeyPrefix}|default`}
              preferredDivisionId={props.preferredDivisionId}
              reloadTeeTimeTabs={props.reloadTeeTimeTabs}
              duplicateBookingModal={duplicateBookingModal}
              updateDuplicateBookingModal={updateDuplicateBookingModal}
              sideBySideSettings={props.sideBySideSettings}
            />
          </DragDropContext>
        );
      }
    }
  };

  function getDivisionGroupedTeeTimes(divisionId: number) {
    if (teeSheetView === "side-by-side") {
      return groupedSideBySideDivisions?.find(
        sideBySideTeeSheetDivision => sideBySideTeeSheetDivision.id === divisionId,
      )?.groupedTeeTimes;
    } else {
      return state.groupedTeeTimes;
    }
  }

  async function checkInAllTournamentParticipants() {
    const tournamentId = tournamentCheckInParticipants.teetime?.slots?.[0]?.tournament_id;
    const startTime = tournamentCheckInParticipants.teetime?.slots?.[0]?.start_time;
    const divisionId = tournamentCheckInParticipants.teetime?.slots?.[0]?.division_id;

    const participantIds: number[] = [];

    const groupedTeeTimes: ITeeTimeDraggable[] = getDivisionGroupedTeeTimes(divisionId);

    if (!groupedTeeTimes) {
      return;
    }

    for (let outerIndex = 0; outerIndex < groupedTeeTimes.length; outerIndex++) {
      const teetime = groupedTeeTimes[outerIndex];

      if (
        Number(teetime.slots[0][0].tournament_id) === Number(tournamentId) &&
        String(teetime.slots[0][0].start_time) === startTime
      ) {
        for (let innerIndex = outerIndex; innerIndex < groupedTeeTimes.length; innerIndex++) {
          if (groupedTeeTimes[innerIndex].slots[0][0].tournament_id !== Number(tournamentId)) {
            break;
          }

          const currentTeeTime = groupedTeeTimes[innerIndex];

          currentTeeTime.slots?.forEach(slot => {
            if (slot.length > 1) {
              slot?.forEach(guestSlot => {
                if (guestSlot.booking_participant_id !== null && String(guestSlot.check_in_status) === "unchecked") {
                  participantIds.push(guestSlot.booking_participant_id);
                }
              });
            } else {
              if (slot[0].booking_participant_id !== null && String(slot[0].check_in_status) === "unchecked") {
                participantIds.push(slot[0].booking_participant_id);
              }
            }
          });
        }
      }
    }

    if (participantIds.length === 0) {
      dispatch(showError(t("All Participants Have Been Checked In")));
      updateCheckInAllShotgunParticipants({
        isOpen: false,
      });
      return;
    }

    const checkInRes = await PutCheckAllParticipant({ booking_participant_ids: participantIds }, true);

    if (checkInRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error Checking In Participants")));
      updateCheckInAllShotgunParticipants({
        isOpen: false,
      });
      return;
    }

    dispatch(showSuccess(t("Successfully Checked In Participants")));
    props.reloadTeeTimeTabs();
  }

  async function checkInAllLeagueParticipants() {
    const leagueId = leagueCheckInParticipants.teetime?.slots?.[0]?.league_id;
    const startTime = leagueCheckInParticipants.teetime?.slots?.[0]?.start_time;
    const divisionId = leagueCheckInParticipants.teetime?.slots?.[0]?.division_id;

    const participantIds: number[] = [];

    const groupedTeeTimes: ITeeTimeDraggable[] = getDivisionGroupedTeeTimes(divisionId);

    if (!groupedTeeTimes) {
      return;
    }

    for (let outerIndex = 0; outerIndex < groupedTeeTimes.length; outerIndex++) {
      const teetime = groupedTeeTimes[outerIndex];

      if (
        Number(teetime.slots[0][0].league_id) === Number(leagueId) &&
        String(teetime.slots[0][0].start_time) === startTime
      ) {
        for (let innerIndex = outerIndex; innerIndex < groupedTeeTimes.length; innerIndex++) {
          if (groupedTeeTimes[innerIndex].slots[0][0].league_id !== Number(leagueId)) {
            break;
          }

          const currentTeeTime = groupedTeeTimes[innerIndex];

          currentTeeTime.slots?.forEach(slot => {
            if (slot.length > 1) {
              slot?.forEach(guestSlot => {
                if (guestSlot.booking_participant_id !== null && String(guestSlot.check_in_status) === "unchecked") {
                  participantIds.push(guestSlot.booking_participant_id);
                }
              });
            } else {
              if (slot[0].booking_participant_id !== null && String(slot[0].check_in_status) === "unchecked") {
                participantIds.push(slot[0].booking_participant_id);
              }
            }
          });
        }
      }
    }

    if (participantIds.length === 0) {
      dispatch(showError(t("All Participants Have Been Checked In")));
      updateCheckInAllShotgunParticipants({
        isOpen: false,
      });
      return;
    }

    const checkInRes = await PutCheckAllParticipant({ booking_participant_ids: participantIds }, true);

    if (checkInRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error Checking In Participants")));
      updateCheckInAllShotgunParticipants({
        isOpen: false,
      });
      return;
    }

    dispatch(showSuccess(t("Successfully Checked In Participants")));
    props.reloadTeeTimeTabs();
  }

  async function checkInAllShotgunParticipants() {
    const shotgunId = shotgunCheckInParticipants.teetime?.slots?.[0]?.shotgun_application_id;
    const startTime = shotgunCheckInParticipants.teetime?.slots?.[0]?.start_time;
    const divisionId = shotgunCheckInParticipants.teetime?.slots?.[0]?.division_id;

    const participantIds: number[] = [];

    const groupedTeeTimes: ITeeTimeDraggable[] = getDivisionGroupedTeeTimes(divisionId);

    if (!groupedTeeTimes) {
      return;
    }

    groupedTeeTimes?.forEach(teetime => {
      if (Number(teetime.slots[0][0].shotgun_application_id) === Number(shotgunId)) {
        teetime.slots?.forEach(slot => {
          if (slot.length > 1) {
            slot?.forEach(guestSlot => {
              if (
                guestSlot.booking_participant_id !== null &&
                String(guestSlot.check_in_status) === "unchecked" &&
                String(guestSlot.start_time) === startTime
              ) {
                participantIds.push(guestSlot.booking_participant_id);
              }
            });
          } else {
            if (
              slot[0].booking_participant_id !== null &&
              String(slot[0].check_in_status) === "unchecked" &&
              String(slot[0].start_time) === startTime
            ) {
              participantIds.push(slot[0].booking_participant_id);
            }
          }
        });
      }
    });

    if (participantIds.length === 0) {
      dispatch(showError(t("All Participants Have Been Checked In")));
      updateCheckInAllShotgunParticipants({
        isOpen: false,
      });
      return;
    }

    const checkInRes = await PutCheckAllParticipant({ booking_participant_ids: participantIds }, true);

    if (checkInRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error Checking In Participants")));
      updateCheckInAllShotgunParticipants({
        isOpen: false,
      });
      return;
    }

    dispatch(showSuccess(t("Successfully Checked In Participants")));
    props.reloadTeeTimeTabs();
  }

  // Get the next tee time's start time
  function getNextTeeTimeStartTime(currentTeeTime: ITeeTime) {
    if (!currentTeeTime) {
      return null;
    }

    const teetimes = getDivisionGroupedTeeTimes(currentTeeTime.division_id);

    if (!teetimes) {
      return null;
    }

    const teeTimeIndex = teetimes.findIndex(teetime => teetime.slots[0][0].tee_time_id === currentTeeTime.id);

    if (teeTimeIndex !== -1) {
      return teetimes[teeTimeIndex + 1]?.start_time;
    }

    return null;
  }

  function handleSelectDivisions(teetime: ITeeTimeTabNew) {
    updateSelectDivisionsPopup({ isOpen: true, teetime: teetime });
  }

  async function handlePrintScorecards() {
    let tournamentId = 0;
    let leagueId = 0;
    let params;

    if (selectDivisionsPopup.teetime.slots[0].league_id) {
      leagueId = selectDivisionsPopup.teetime.slots[0].league_id;
      params = {
        league_id: leagueId,
        tee_sheet_id: selectDivisionsPopup.teetime?.slots[0]?.tee_sheet_id,
        facility_id: facilityStore?.facility?.id,
        start_time: selectDivisionsPopup.teetime?.slots[0]?.start_time,
        frontDivision: selectDivisionsPopup.selectedFrontNineDivision,
        backDivision: selectDivisionsPopup.selectedBackNineDivision,
      };
    } else if (selectDivisionsPopup.teetime.slots[0].tournament_id) {
      tournamentId = selectDivisionsPopup.teetime.slots[0].tournament_id;
      params = {
        tournament_id: tournamentId,
        tee_sheet_id: selectDivisionsPopup.teetime.slots[0].tee_sheet_id,
        facility_id: facilityStore?.facility?.id,
        start_time: selectDivisionsPopup.teetime?.slots[0]?.start_time,
        frontDivision: selectDivisionsPopup.selectedFrontNineDivision,
        backDivision: selectDivisionsPopup.selectedBackNineDivision,
      };
    }

    const eventScorecardsRes = await GetEventScorecards(params, true);

    if (eventScorecardsRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error printing event scorecard")));
      return;
    }

    window.open().document.write(String(eventScorecardsRes.data));
  }

  async function addTournamentPlayer(name: string, customer_id: number, slot: ITeeTimeSlotNew) {
    const params = {
      player_name: name,
      organizer_id: customer_id,
      tee_time_slot_id: slot.tee_time_slot_id,
      tournament_id: slot.tournament_id,
    };

    const addPlayerRes = await AddTournamentPlayer(params, true);

    if (addPlayerRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error adding tournament player")));
      closeAddTournamentPlayer();
      return;
    }

    dispatch(showSuccess(t("Successfully added player")));
    closeAddTournamentPlayer();
    props.reloadTeeTimeTabs();
  }

  function editLotteryRequest(lotteryRequestId: number) {
    const requests = [...lotteryRequestsModal.requests];
    const updatedRequests = requests?.map((request: Record<string, any>) => ({
      ...request,
      edit: request?.id === lotteryRequestId ? !request?.edit : false,
      players: [
        {
          id: request?.customer_id_one,
          name: request?.customer_name_one,
          customer_type: request?.players[0]?.customer_type ?? null,
        },
        {
          id: request?.customer_id_two,
          name: request?.customer_name_two,
          customer_type:
            request?.customer_id_two !== request?.customer_id_one ? request?.players[1]?.customer_type : null,
        },
        {
          id: request?.customer_id_three,
          name: request?.customer_name_three,
          customer_type:
            request?.customer_id_three !== request?.customer_id_one ? request?.players[2]?.customer_type : null,
        },
        {
          id: request?.customer_id_four,
          name: request?.customer_name_four,
          customer_type:
            request?.customer_id_four !== request?.customer_id_one ? request?.players[3]?.customer_type : null,
        },
      ],
    }));
    ReactDOM.unstable_batchedUpdates(() => {
      void updateLotteryRequestsModal({ requests: updatedRequests });
      setLotteryRequest(prev => ({
        ...prev,
        golferSearchQuery1: "",
        golferSearchQuery2: "",
        golferSearchQuery3: "",
        golferSearchQuery4: "",
      }));
    });
  }

  function handleSelectLotteryCustomer(customer: ICustomer, buildIndex: number, playerIndex: number) {
    const updatedBuildData = [...lotteryRequestsModal.requests];

    if (updatedBuildData[buildIndex]) {
      if (updatedBuildData[buildIndex]?.players[playerIndex]) {
        updatedBuildData[buildIndex].players[playerIndex] = {
          ...updatedBuildData[buildIndex].players[playerIndex],
          id: customer?.id,
          name: customer?.full_name,
          customer_type: customer?.customer_type,
        };
        updatedBuildData[buildIndex] = { ...updatedBuildData[buildIndex] };
        void updateLotteryRequestsModal({ requests: updatedBuildData });
      }
    }
  }

  function handleRemoveLotteryCustomer(buildIndex: number, playerIndex: number) {
    const updatedBuildData = [...lotteryRequestsModal.requests];

    if (updatedBuildData[buildIndex]) {
      if (updatedBuildData[buildIndex]?.players[playerIndex]) {
        updatedBuildData[buildIndex].players[playerIndex] = {
          ...updatedBuildData[buildIndex]?.players[playerIndex],
          id: null,
          name: null,
          customer_type: null,
        };
        updatedBuildData[buildIndex] = { ...updatedBuildData[buildIndex] };
        ReactDOM.unstable_batchedUpdates(() => {
          void updateLotteryRequestsModal({ requests: updatedBuildData });
          setLotteryRequest(prev => ({
            ...prev,
            golferSearchQuery1: "",
            golferSearchQuery2: "",
            golferSearchQuery3: "",
            golferSearchQuery4: "",
          }));
        });
      }
    }
  }

  async function saveLotteryRequest(lotteryRequestId: number, players: Array<{ id: number; name: string }>) {
    if (!players[0]?.id) {
      dispatch(showError(t("Player required in slot 1")));
      return;
    }
    const foundLottery = state?.teeSheetLotteries?.find(lottery => lottery?.id === lotteryRequestsModal?.lottery_id);

    if (!foundLottery?.allow_guests && players?.filter(player => player?.id === players[0]?.id)?.length > 1) {
      dispatch(showError(t("Guests not allowed")));
      return;
    }

    const params = {
      id: lotteryRequestId,
      customer_id: players[0]?.id ?? null,
      customer_id_one: players[0]?.id ?? null,
      customer_id_two: players[1]?.id ?? null,
      customer_id_three: players[2]?.id ?? null,
      customer_id_four: players[3]?.id ?? null,
      quantity: players?.filter(player => player?.id)?.length,
    };

    dispatch(enqueue());
    const res = await PutTeeTimeLotteryRequest(params, false);
    if (res?.status !== StatusCode.OK) {
      dispatch(dequeue());
      dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error updating lottery request")));
      return;
    }

    //Fetch updated lottery requests
    const requestRes = await GetTeeTimeLotteryRequest({ lottery_id: lotteryRequestsModal?.lottery_id }, true);
    if (requestRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting lottery requests")));
      return;
    }
    dispatch(dequeue());

    const updatedRequests = requestRes?.data?.map((data: Record<string, any>) => ({
      ...data,
      edit: false,
      customer_name_one: data?.customer_one?.full_name,
      customer_name_two: data?.customer_id_two === data?.customer_id_one ? t("Guest") : data?.customer_two?.full_name,
      customer_name_three:
        data?.customer_id_three === data?.customer_id_one ? t("Guest") : data?.customer_three?.full_name,
      customer_name_four:
        data?.customer_id_four === data?.customer_id_one ? t("Guest") : data?.customer_four?.full_name,
      players: [
        {
          id: data?.customer_id_one,
          name: data?.customer_one?.full_name,
          customer_type: data?.customer_one?.customer_type,
        },
        {
          id: data?.customer_id_two,
          name: data?.customer_id_two === data?.customer_id_one ? t("Guest") : data?.customer_two?.full_name,
          customer_type: data?.customer_two?.customer_type,
        },
        {
          id: data?.customer_id_three,
          name: data?.customer_id_three === data?.customer_id_one ? t("Guest") : data?.customer_three?.full_name,
          customer_type: data?.customer_three?.customer_type,
        },
        {
          id: data?.customer_id_four,
          name: data?.customer_id_four === data?.customer_id_one ? t("Guest") : data?.customer_four?.full_name,
          customer_type: data?.customer_four?.customer_type,
        },
      ],
    }));

    ReactDOM.unstable_batchedUpdates(() => {
      void updateLotteryRequestsModal({ requests: updatedRequests });
      setLotteryRequest(prev => ({
        ...prev,
        golferSearchQuery1: "",
        golferSearchQuery2: "",
        golferSearchQuery3: "",
        golferSearchQuery4: "",
      }));
    });
    dispatch(showSuccess(t("Successfully updated lottery request")));
  }

  function addLotteryGuest(buildIndex: number, playerIndex: number, players: Array<{ id: number; name: string }>) {
    const updatedBuildData = [...lotteryRequestsModal.requests];

    if (updatedBuildData[buildIndex]) {
      if (updatedBuildData[buildIndex]?.players[playerIndex]) {
        updatedBuildData[buildIndex].players[playerIndex] = {
          ...updatedBuildData[buildIndex]?.players[playerIndex],
          id: players[0]?.id ?? updatedBuildData[buildIndex]?.players[0]?.id,
          name: t("Guest") ?? updatedBuildData[buildIndex]?.players[playerIndex]?.name,
        };
        updatedBuildData[buildIndex] = { ...updatedBuildData[buildIndex] };
        ReactDOM.unstable_batchedUpdates(() => {
          void updateLotteryRequestsModal({ requests: updatedBuildData });
          setLotteryRequest(prev => ({
            ...prev,
            golferSearchQuery1: "",
            golferSearchQuery2: "",
            golferSearchQuery3: "",
            golferSearchQuery4: "",
          }));
        });
      }
    }
  }

  const debouncedLotterySearch = useMemo(
    () =>
      debounce((query: string, queryKey: string) => {
        setLotteryRequest(prev => ({ ...prev, [queryKey]: query }));
      }, 200),
    [],
  );

  async function handleBuildLottery() {
    const res = await PostTeeTimeLotteryBuild(
      { date: buildLotteryPopup?.date, lottery_id: buildLotteryPopup?.lotteryId },
      true,
    );
    if (res?.status !== StatusCode.OK) {
      dispatch(showError(t("Error building lottery")));
      return;
    }
    const filteredLotteryBuild = lotteryBuild?.filter(build => build?.lottery_id !== buildLotteryPopup?.lotteryId);
    dispatch(showSuccess(t("Lottery built successfully")));
    ReactDOM.unstable_batchedUpdates(() => {
      void closeBuildLotteryPopup();
      setLotteryBuild([...filteredLotteryBuild, ...res.data]);
    });
  }

  async function handleFinalizeLottery() {
    const res = await PostTeeTimeLotteryFinalize({ lottery_id: finalizeLotteryPopup?.lotteryId }, true);
    if (res?.status !== StatusCode.OK) {
      dispatch(showError(t("Error finalizing lottery")));
      return;
    }
    // const filteredLotteryBuild = lotteryBuild?.filter(build => build?.lottery_id !== buildLotteryPopup?.lotteryId);
    dispatch(showSuccess(t("Lottery finalizing successfully")));
    ReactDOM.unstable_batchedUpdates(() => {
      void closeFinalizeLotteryPopup();
      props.reloadTeeTimeTabs();
    });
  }

  async function deleteLotteryRequest() {
    dispatch(enqueue());
    const res = await DeleteTeeTimeLotteryRequest({ id: deleteLotteryRequestPopup?.request?.id }, false);
    if (res?.status !== StatusCode.OK) {
      dispatch(dequeue());
      dispatch(showError(t("Error deleting lottery request")));
      return;
    }
    //Fetch updated lottery requests
    const requestRes = await GetTeeTimeLotteryRequest({ lottery_id: lotteryRequestsModal?.lottery_id }, false);
    if (requestRes?.status !== StatusCode.OK) {
      dispatch(dequeue());
      dispatch(showError(t("Error getting lottery requests")));
      return;
    }

    //If a build exists with the matching request id, rebuild the lottery
    if (lotteryBuild?.some(build => build?.lottery_request_id === deleteLotteryRequestPopup?.request?.id)) {
      const buildRes = await PostTeeTimeLotteryBuild(
        { date: lotteryRequestsModal?.date, lottery_id: deleteLotteryRequestPopup?.request?.lottery_id },
        true,
      );
      if (buildRes?.status !== StatusCode.OK) {
        dispatch(showError(t("Error building lottery")));
        return;
      }
      const filteredLotteryBuild = lotteryBuild?.filter(
        build => build?.lottery_id !== deleteLotteryRequestPopup?.request?.lottery_id,
      );
      setLotteryBuild([...filteredLotteryBuild, ...buildRes.data]);
    }

    dispatch(dequeue());

    const updatedRequests = requestRes?.data?.map((data: Record<string, any>) => ({
      ...data,
      edit: false,
      customer_name_one: data?.customer_one?.full_name,
      customer_name_two: data?.customer_id_two === data?.customer_id_one ? t("Guest") : data?.customer_two?.full_name,
      customer_name_three:
        data?.customer_id_three === data?.customer_id_one ? t("Guest") : data?.customer_three?.full_name,
      customer_name_four:
        data?.customer_id_four === data?.customer_id_one ? t("Guest") : data?.customer_four?.full_name,
      players: [
        {
          id: data?.customer_id_one,
          name: data?.customer_one?.full_name,
          customer_type: data?.customer_one?.customer_type,
        },
        {
          id: data?.customer_id_two,
          name: data?.customer_id_two === data?.customer_id_one ? t("Guest") : data?.customer_two?.full_name,
          customer_type: data?.customer_two?.customer_type,
        },
        {
          id: data?.customer_id_three,
          name: data?.customer_id_three === data?.customer_id_one ? t("Guest") : data?.customer_three?.full_name,
          customer_type: data?.customer_three?.customer_type,
        },
        {
          id: data?.customer_id_four,
          name: data?.customer_id_four === data?.customer_id_one ? t("Guest") : data?.customer_four?.full_name,
          customer_type: data?.customer_four?.customer_type,
        },
      ],
    }));

    ReactDOM.unstable_batchedUpdates(() => {
      void updateLotteryRequestsModal({ requests: updatedRequests });
      setLotteryRequest(prev => ({
        ...prev,
        golferSearchQuery1: "",
        golferSearchQuery2: "",
        golferSearchQuery3: "",
        golferSearchQuery4: "",
      }));
    });

    dispatch(showSuccess(t("Lottery request deleted successfully")));
    void closeDeleteLotteryRequestPopup();
  }

  async function handleEditShotgun({start_time, league_id, tournament_id, duration, lead_time, shotgun_application_id, tee_sheet_id}: {start_time: string; league_id: number; tournament_id: number; duration: string; lead_time: string; shotgun_application_id: number; tee_sheet_id: number}) {
    if (!start_time) {
      dispatch(showError(t("Start time required")));
      return;
    } else if (!duration) {
      dispatch(showError(t("Duration time required")));
      return;
    } else if (!lead_time) {
      dispatch(showError(t("Lead time required")));
      return;
    }
    
    const durationTime = duration;
    const durationSplit = durationTime.split(":");
    const durationMins = +durationSplit[0] * 60 + +durationSplit[1];
    const leadTime = lead_time;
    const leadTimeSplit = leadTime.split(":");
    const leadTimeMins = +leadTimeSplit[0] * 60 + +leadTimeSplit[1];
    const res = await PutEditShotgunApply({
      start_time,
      league_id,
      duration: durationMins,
      lead_time: leadTimeMins,
      shotgun_application_id,
      tournament_id,
      tee_sheet_id,

    }, true);

    if (res?.status !== StatusCode.OK) {
      dispatch(showError(errorMessage(res, t("Error editing shotgun"))));
      return;
    }
    dispatch(showSuccess(t("Successfully updated shotgun event")));
    props.reloadTeeTimeTabs();
    void closeEditShotgunPopup();
  }

  return (
    <>
      {renderTeeSheetTabsContainer()}

      <Portal isMounted={moveTeeTimeBookingConfirmation.isOpen}>
        <Popup
          open={moveTeeTimeBookingConfirmation.isOpen}
          title=""
          description=""
          type="warning"
          onOk={startMoveBookingProcess}
          okText={t("Confirm")}
          cancelText={t("Cancel")}
          onCancel={handleCancelTeeTimeMove}
        >
          <div className="tee-sheet-move-confirmation-heading">{t("Move Booking")}</div>
          <div className="tee-sheet-move-confirmation-description">
            {t("Are you sure you want to move this tee-time?")}
          </div>
          <div className="pt-2">
            <Checkbox
              size="small"
              checked={moveTeeTimeBookingConfirmation.sendConfirmation}
              onChange={() =>
                updateMoveTeeTimeBookingConfirmationPopup({
                  sendConfirmation: !moveTeeTimeBookingConfirmation.sendConfirmation,
                })
              }
              label={t("Resend Confirmation?")}
            />
          </div>
          {moveTeeTimeBookingConfirmation.displayHoleConversionWarning && (
            <div className="pt-3">
              <div className="tee-sheet-move-confirmation-heading">{t("Conversion required")}</div>
              <div className="tee-sheet-move-confirmation-description">
                {`${t("Are you ok with this booking being converted from")} ${
                  moveTeeTimeBookingConfirmation.draggingBookingHoles
                } ${t("Holes To")} ${moveTeeTimeBookingConfirmation.draggingBookingHoles === 9 ? 18 : 9}?`}
              </div>
            </div>
          )}
        </Popup>
      </Portal>
      {/* MODALS */}
      {/* NEW BOOKING MODAL */}
      <BookingModalNew
        cartsAvailable={newBookingModal.cartsAvailable}
        slot={newBookingModal.slot}
        getNextTeeTimeStartTime={getNextTeeTimeStartTime}
        loading={newBookingModal.loading}
        loadingText={newBookingModal.loadingText}
        reloadTeeTimes={props.reloadTeeTimeTabs}
        reloadActionTeeTimeTabs={(addTeetimeTabAction: TLoadingAddBookingsAction) =>
          props.reloadActionTeeTimeTabs(addTeetimeTabAction, true)
        }
        bookingGroupId={newBookingModal?.groupBookingId}
      />

      <LotteryRequestModal
        cartsAvailable={newBookingModal.cartsAvailable}
        slot={newBookingModal.slot}
        getNextTeeTimeStartTime={getNextTeeTimeStartTime}
        loading={newBookingModal.loading}
        loadingText={newBookingModal.loadingText}
        reloadTeeTimes={props.reloadTeeTimeTabs}
        reloadActionTeeTimeTabs={(addTeetimeTabAction: TLoadingAddBookingsAction) =>
          props.reloadActionTeeTimeTabs(addTeetimeTabAction, true)
        }
        lottery={newBookingModal.lottery}
        lotteryTeeTimes={newBookingModal.lotteryTeeTimes}
        lotteryBuilds={lotteryBuild}
      />

      {/* LOTTERY REQUEST MODAL */}
      <Portal isMounted={lotteryRequestsModal.isOpen}>
        <Sheet
          open={lotteryRequestsModal.isOpen}
          size={lotteryRequestsModal?.requests?.length === 0 ? "small" : "medium"}
          title={t("Lottery Requests")}
          onCancel={closeLotteryRequestsModal}
          backDropCancel={false}
          okDisabled={lotteryRequestsModal?.requests?.length === 0}
          closable
        >
          <div className="tee-sheet-lottery-main-container">
            {lotteryRequestsModal?.requests?.length === 0 ? (
              <Callout
                title={t("No Requests Found")}
                content={t("Create new lottery requests to view and edit")}
                type="info"
              />
            ) : (
              lotteryRequestsModal?.requests?.map((request, buildIndex) => {
                return (
                  <div key={buildIndex} className="tee-sheet-lottery-container">
                    <div className="tee-sheet-lottery-header">
                      <div className="tee-sheet-lottery-customer-container">
                        <p>{request?.customer_name_one}</p>
                        <span className="tee-sheet-lottery-header-customer-type">
                          {request?.customer_one?.customer_type}
                        </span>
                      </div>
                      <div className="tee-sheet-lottery-customer-container">
                        <p>{request?.customer_name_two}</p>
                        <span
                          className="tee-sheet-lottery-header-customer-type"
                          style={{ visibility: request?.customer_name_two === "Guest" ? "hidden" : "visible" }}
                        >
                          {request?.customer_two?.customer_type}
                        </span>
                      </div>
                      <div className="tee-sheet-lottery-customer-container">
                        <p>{request?.customer_name_three}</p>
                        <span
                          className="tee-sheet-lottery-header-customer-type"
                          style={{ visibility: request?.customer_name_three === "Guest" ? "hidden" : "visible" }}
                        >
                          {request?.customer_three?.customer_type}
                        </span>
                      </div>
                      <div className="tee-sheet-lottery-customer-container">
                        <p>{request?.customer_name_four}</p>
                        <span
                          className="tee-sheet-lottery-header-customer-type"
                          style={{ visibility: request?.customer_name_four === "Guest" ? "hidden" : "visible" }}
                        >
                          {request?.customer_four?.customer_type}
                        </span>
                      </div>

                      <div className="ml-auto flex gap-2 items-center">
                        <Badge size="small" type="primary">
                          {request?.holes} {t("holes")}
                        </Badge>
                        <Badge size="small" type="gray">
                          {moment(request?.start_time_preference_one as string, "HH:mm")?.format("LT")}
                        </Badge>
                        {request?.start_time_preference_two ? (
                          <Badge size="small" type="gray">
                            {moment(request?.start_time_preference_two as string, "HH:mm")?.format("LT")}
                          </Badge>
                        ) : null}
                        {request?.start_time_preference_three ? (
                          <Badge size="small" type="gray">
                            {moment(request?.start_time_preference_three as string, "HH:mm")?.format("LT")}
                          </Badge>
                        ) : null}
                        {request?.start_time_preference_window ? (
                          <Badge size="small" type="gray">
                            {request?.start_time_preference_window} {t("hour window")}
                          </Badge>
                        ) : null}
                        <Button type="secondary" size="small" onClick={() => editLotteryRequest(request?.id as number)}>
                          {request?.edit ? t("Close") : t("Edit")}{" "}
                          <FontAwesomeIcon className="ml-1" size="1x" icon={faPenToSquare} />
                        </Button>
                      </div>
                    </div>

                    <div
                      className={classNames("tee-sheet-lottery-edit-close", {
                        "tee-sheet-lottery-edit": request?.edit,
                      })}
                    >
                      <FormLayout>
                        <FormLayout.Group>
                          {request?.players?.map(
                            (player: { id: number; name: string; customer_type: string }, index: number) => {
                              const queryKey = "golferSearchQuery".concat(`${index + 1}`);
                              const searchResultKey = "golferSearchResult".concat(`${index + 1}`);
                              if (player?.id) {
                                return (
                                  <GolferCard
                                    key={index}
                                    closable
                                    removeGolfer={() => handleRemoveLotteryCustomer(buildIndex, index)}
                                    name={player?.name}
                                    className="whitespace-nowrap"
                                    customerType={player?.customer_type}
                                  />
                                );
                              } else {
                                if (request?.edit) {
                                  return (
                                    <Select
                                      key={index}
                                      placeholder={t("Search...")}
                                      showSearch
                                      onSearch={(query: string) => debouncedLotterySearch(query, queryKey)}
                                      onChange={(_, customer: ICustomer) =>
                                        handleSelectLotteryCustomer(customer, buildIndex, index)
                                      }
                                      allowClear
                                      searchValue={lotteryRequest[queryKey]}
                                      showDropDownOnFocus={true}
                                      searching={lotteryRequest?.searching}
                                      usePortal
                                    >
                                      <div
                                        className="ui-select-dropdown-list-item"
                                        onClick={() =>
                                          addLotteryGuest(
                                            buildIndex,
                                            index,
                                            request?.players as { id: number; name: string }[],
                                          )
                                        }
                                      >
                                        <p className="text-md">{t("Guest")}</p>
                                      </div>
                                      {lotteryRequest[searchResultKey]?.map((golfer: ICustomer, i: number) => {
                                        //Banned golfer
                                        if (golfer && golfer?.banned) {
                                          return (
                                            <div
                                              key={i}
                                              className="flex justify-between bg-gray-100 cursor-not-allowed"
                                              style={{ padding: "8px 16px" }}
                                            >
                                              <div>
                                                <div className="font-semibold text-lg">{golfer?.full_name}</div>
                                                <div className="text-sm text-gray-500">{golfer.customer_type}</div>
                                                <div className="text-sm text-gray-500">{golfer.email}</div>
                                                <div className="text-sm text-gray-500">
                                                  {golfer.phone ? golfer.phone : null}
                                                </div>
                                              </div>
                                              <div className="flex flex-column justify-between">
                                                <div className="flex flex-column gap-2">
                                                  {golfer.has_high_priority_notes ? (
                                                    <div
                                                      className="font-medium text-error self-end gap-2 flex"
                                                      style={{ alignItems: "center" }}
                                                      onClick={e => {
                                                        e.stopPropagation();
                                                        window.open(`/admin/customers/${String(golfer.id)}/notes`);
                                                      }}
                                                    >
                                                      <FontAwesomeIcon icon={faCircleExclamation} size="lg" />
                                                      <p>{t("Click to view notes")}</p>
                                                    </div>
                                                  ) : (
                                                    <div />
                                                  )}
                                                  {golfer?.banned ? (
                                                    <div className="ml-auto">
                                                      <Badge size="small" type="warning">
                                                        Banned
                                                      </Badge>
                                                    </div>
                                                  ) : null}
                                                </div>
                                                <div className="font-medium text-base text-gray-500 self-end">
                                                  {golfer.member_code}
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        } else if (golfer) {
                                          return (
                                            <Option key={i} value={golfer.id} extraValues={golfer}>
                                              <div className="flex justify-between">
                                                <div>
                                                  <div className="font-semibold text-lg">{golfer?.full_name}</div>
                                                  <div className="text-sm text-gray-500">{golfer.customer_type}</div>
                                                  <div className="text-sm text-gray-500">{golfer.email}</div>
                                                  <div className="text-sm text-gray-500">
                                                    {golfer.phone ? golfer.phone : null}
                                                  </div>
                                                </div>
                                                <div className="flex flex-column justify-between">
                                                  {golfer.has_high_priority_notes ? (
                                                    <div
                                                      className="font-medium text-error self-end gap-2 flex"
                                                      style={{ alignItems: "center" }}
                                                      onClick={e => {
                                                        e.stopPropagation();
                                                        window.open(`/admin/customers/${String(golfer.id)}/notes`);
                                                      }}
                                                    >
                                                      <FontAwesomeIcon icon={faCircleExclamation} size="lg" />
                                                      <p>{t("Click to view notes")}</p>
                                                    </div>
                                                  ) : (
                                                    <div />
                                                  )}
                                                  <div className="font-medium text-base text-gray-500 self-end">
                                                    {golfer.member_code}
                                                  </div>
                                                </div>
                                              </div>
                                            </Option>
                                          );
                                        }
                                      })}
                                    </Select>
                                  );
                                }
                              }
                            },
                          )}
                        </FormLayout.Group>
                        <FormLayout.Group>
                          <div className="flex items-center">
                            <Button
                              type="error-outline"
                              size="medium"
                              className="ml-auto"
                              onClick={() => updateDeleteLotteryRequestPopup({ isOpen: true, request: request })}
                            >
                              {t("Delete")}
                            </Button>
                            <Button
                              type="primary"
                              size="medium"
                              onClick={() =>
                                saveLotteryRequest(
                                  request?.id as number,
                                  request?.players as { id: number; name: string }[],
                                )
                              }
                            >
                              {t("Save")}
                            </Button>
                          </div>
                        </FormLayout.Group>
                      </FormLayout>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Sheet>
      </Portal>

      {/* NO SHOW MODAL */}
      <Portal isMounted={noShowModal.isOpen}>
        <Popup
          open={noShowModal.isOpen}
          type="info"
          title={t("Mark As No Show")}
          description={t("Select the type of no show you would like to apply to this customer.")}
          onOk={() => (noShowModal?.chargeNoShow ? getNoShowBooking() : handleNoShow(false))}
          okText={noShowModal?.chargeNoShow ? t("Continue") : t("Confirm")}
          okDisabled={
            !noShowModal.type || !noShowModal.bookingParticipantIds || noShowModal?.bookingParticipantIds?.length === 0
          }
          cancelText={t("Cancel")}
          onCancel={() =>
            updateNoShowModal({
              isOpen: false,
              type: "charge",
              bookingParticipantIds: [],
              bookingId: null,
              teeTimeIndex: null,
            })
          }
        >
          <Select defaultValue={noShowModal.type} onChange={(value: string) => updateNoShowModal({ type: value })}>
            <Select.Option value={"charge"} name={"Charge"}>
              <span>{t("Charge")}</span>
            </Select.Option>
            <Select.Option value={"defer"} name={"Defer Charge"}>
              <span>{t("Defer Charge")}</span>
            </Select.Option>
            <Select.Option value={"weather"} name={"Weather"}>
              <span>{t("Weather")}</span>
            </Select.Option>
          </Select>
          <br />
          {noShowModal?.slotGroup
            ?.filter((filteredSlot: ITeeTimeSlotNew) => filteredSlot?.check_in_status === "unchecked")
            ?.map((slot: ITeeTimeSlotNew, index: number) => {
              return (
                <div
                  key={index}
                  className="tee-time-no-show-row "
                  onClick={() => handleNoShowCheckboxChange(slot?.booking_participant_id)}
                >
                  <div className="pointer-events-none">
                    <Checkbox
                      size="small"
                      checked={noShowModal?.bookingParticipantIds?.includes(slot?.booking_participant_id)}
                      onChange={() => handleNoShowCheckboxChange(slot?.booking_participant_id)}
                      labelHidden
                    />
                  </div>
                  <p>
                    {slot?.customer_first_name} {slot?.customer_last_name} {slot?.guest ? " - Guest" : ""}
                  </p>
                </div>
              );
            })}
          <br />
          <span className="flex flex-row justify-between items-center ml-3 mr-3 mt-2">
            <div className="flex justify-right">
              <Checkbox
                size="small"
                label={t("No Show Email")}
                id="sendNoShowChargeEmail"
                checked={noShowModal?.sendNoShowChargeEmail}
                onChange={e => updateNoShowModal({ sendNoShowChargeEmail: e.target.checked })}
              />
            </div>
            <div className="flex justify-right">
              <Checkbox
                size="small"
                checked={noShowModal?.chargeNoShow}
                onChange={e => updateNoShowModal({ chargeNoShow: e.target.checked })}
                label={t("Charge Customers")}
              />
            </div>
          </span>
        </Popup>
      </Portal>

      <Portal isMounted={noCreditCardPopup.isOpen}>
        <Popup
          open={noCreditCardPopup.isOpen}
          type="info"
          title={t("No Payment Method on Booking")}
          description={t("There is no payment method saved on this booking")}
          onOk={closeNoCreditCardPopup}
          okText={t("Close")}
          stacked
        />
      </Portal>

      {/* CHARGE NO SHOW MODAL */}
      <Portal isMounted={noShowChargeModal?.isOpen}>
        <Sheet
          open={noShowChargeModal?.isOpen}
          size="medium"
          closable
          title={t("Charge No Show")}
          onCancel={() => {
            closeNoShowChargeModal();
            cancelRequest();
          }}
          onOk={() => updateNoShowChargeModal({ confirmationPopup: true })}
          cancelText={t("Cancel")}
          okText={t("Charge")}
          stacked
          okDisabled={
            !noShowChargeModal?.paymentMethod ||
            !noShowChargeModal?.selectedProduct ||
            noShowModal?.bookingParticipantIds?.length < 1
              ? true
              : false
          }
          overflow
        >
          {noShowChargeModal?.booking === undefined ? (
            <div style={{ display: "flex", margin: "auto", height: "100%" }}>
              <div className="text-center m-auto">
                <div style={{ height: "32px" }}>
                  <Spin />
                </div>
                <div>{t("Loading...")}</div>
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col">
              {noShowModal?.slotGroup
                ?.filter((filteredSlot: ITeeTimeSlotNew) => filteredSlot?.check_in_status === "unchecked")
                ?.map((slot: ITeeTimeSlotNew, index: number) => {
                  return (
                    <div
                      key={index}
                      className="tee-time-no-show-row "
                      onClick={() => handleNoShowCheckboxChange(slot?.booking_participant_id)}
                    >
                      <div className="pointer-events-none">
                        <Checkbox
                          size="small"
                          checked={noShowModal?.bookingParticipantIds?.includes(slot?.booking_participant_id)}
                          onChange={() => handleNoShowCheckboxChange(slot?.booking_participant_id)}
                          labelHidden
                        />
                      </div>
                      <p>
                        {slot?.customer_first_name} {slot?.customer_last_name} {slot?.guest ? " - Guest" : ""}
                      </p>
                    </div>
                  );
                })}
              <Select
                label={t("secure.facility.tee_sheet.tee_sheet_booking_details.060")}
                onChange={(productId, product) => updateNoShowChargeModal({ selectedProduct: product })}
                defaultValue={noShowChargeModal.selectedProduct?.id}
              >
                {noShowChargeModal?.noShowProducts?.length >= 1 &&
                  noShowChargeModal?.noShowProducts?.map((item: IProduct) => {
                    const productTitle = item?.title;
                    const preferredTitle = item?.preferred_title;
                    return (
                      item.variants &&
                      item.variants
                        .filter(variantFiltered => variantFiltered.price > 0)
                        .map((variant, i) => {
                          return (
                            <Select.Option key={i} value={variant.id} name={variant.title} extraValues={variant}>
                              <div>
                                <span>{preferredTitle ? preferredTitle : productTitle} </span>
                                {productTitle !== variant.title && (
                                  <span className="text-subdued text-sm">{variant.title}</span>
                                )}
                                <span>
                                  {" "}
                                  - <LocaleCurrency currency="cad" amount={variant.price} />
                                </span>
                              </div>
                            </Select.Option>
                          );
                        })
                    );
                  })}
              </Select>

              <div className="mt-auto text-lg">
                <ul>
                  <li className="flex justify-end">
                    <span className="mr-4 text-medium">{noShowChargeModal?.selectedProduct?.title}</span>
                    <LocaleCurrency currency="cad" amount={noShowChargeModal?.selectedProduct?.price ?? 0} />
                  </li>
                  <li className="flex justify-end">
                    <span className="mr-4 text-medium">
                      {t("secure.facility.tee_sheet.tee_sheet_booking_details.063")}
                    </span>
                    <span>{noShowModal?.bookingParticipantIds?.length}</span>
                  </li>
                  <li className="flex justify-end text-bold">
                    <span className="mr-4">{t("secure.facility.tee_sheet.tee_sheet_booking_details.064")}</span>
                    <LocaleCurrency
                      currency="cad"
                      amount={
                        noShowChargeModal?.selectedProduct
                          ? noShowModal?.bookingParticipantIds?.length * noShowChargeModal?.selectedProduct?.price
                          : 0
                      }
                    />
                  </li>
                </ul>
              </div>
            </div>
          )}
        </Sheet>
      </Portal>

      {/* Create Tee Time Lottery */}
      <Portal isMounted={lotteryModal.isOpen}>
        <Sheet
          title={t("Create Lottery")}
          open={lotteryModal.isOpen}
          size="small"
          onCancel={closeLotteryModal}
          onOk={createLottery}
          okText={t("Create")}
          overflow
        >
          <FormLayout>
            <FormLayout.Group>
              <DatePickerInput
                months={1}
                position="left"
                startingDate={lotteryModal?.openDate}
                disablePastDays
                setStartingDate={(returnDate: Date) => updateLotteryModal({ openDate: returnDate })}
                rightAlignedCalendar
                label={t("Open Date")}
              />
              <DatePickerInput
                months={1}
                position="left"
                startingDate={lotteryModal?.closeDate}
                disablePastDays
                setStartingDate={(returnDate: Date) => updateLotteryModal({ closeDate: returnDate })}
                rightAlignedCalendar
                label={t("Submission Deadline")}
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <TimePick
                label={t("Start Time")}
                value={lotteryModal.startTime}
                onChange={timeString => updateLotteryModal({ startTime: timeString })}
                size="large"
              />
              <TimePick
                label={t("End Time")}
                value={lotteryModal.endTime}
                onChange={timeString => updateLotteryModal({ endTime: timeString })}
                size="large"
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <SelectNew
                label={t("Number of Time Preferences")}
                id="number_of_time_preferences"
                onChange={val => updateLotteryModal({ timePreferences: val })}
                value={lotteryModal.timePreferences}
                options={[1, 2, 3].map(val => ({ id: val, label: val }))}
                placeholder={t("Number of time preferences")}
                disabled={lotteryModal?.allowTimePreferenceWindow}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Toggle
                size="medium"
                labelTop="Allow Time Preference Window"
                tooltip={t("Flexibility window before and after the requested tee time")}
                checked={!!lotteryModal?.allowTimePreferenceWindow}
                onChange={e =>
                  updateLotteryModal({
                    allowTimePreferenceWindow: e.target.checked,
                    timePreferences: e.target?.checked ? 1 : lotteryModal?.timePreferences,
                  })
                }
              />
              <Toggle
                size="medium"
                labelTop="Allow Guests"
                checked={!!lotteryModal?.allowGuests}
                onChange={e => updateLotteryModal({ allowGuests: e.target.checked })}
              />
            </FormLayout.Group>
          </FormLayout>
        </Sheet>
      </Portal>

      {/* Edit Lottery Modal */}
      <Portal isMounted={editLotteryModal.isOpen}>
        <Sheet
          title={t("Edit Lottery")}
          open={editLotteryModal.isOpen}
          size="small"
          onCancel={closeEditLotteryModal}
          onOk={updateLottery}
          okText={t("Save")}
          overflow
        >
          <FormLayout>
            <FormLayout.Group>
              <DatePickerInput
                months={1}
                position="left"
                startingDate={editLotteryModal?.openDate}
                disablePastDays
                setStartingDate={(returnDate: Date) => updateEditLotteryModal({ openDate: returnDate })}
                rightAlignedCalendar
                label={t("Open Date")}
              />
              <DatePickerInput
                months={1}
                position="left"
                startingDate={editLotteryModal?.closeDate}
                disablePastDays
                setStartingDate={(returnDate: Date) => updateEditLotteryModal({ closeDate: returnDate })}
                rightAlignedCalendar
                label={t("Submission Deadline")}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <SelectNew
                label={t("Number of Time Preferences")}
                id="number_of_time_preferences"
                onChange={val => updateEditLotteryModal({ timePreferences: val })}
                value={editLotteryModal.timePreferences}
                options={[1, 2, 3].map(val => ({ id: val, label: val }))}
                placeholder={t("Number of time preferences")}
                disabled={editLotteryModal?.allowTimePreferenceWindow}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Toggle
                size="medium"
                labelTop="Allow Time Preference Window"
                tooltip={t("Flexibility window before and after the requested tee time")}
                checked={!!editLotteryModal?.allowTimePreferenceWindow}
                onChange={e =>
                  updateEditLotteryModal({
                    allowTimePreferenceWindow: e.target.checked,
                    timePreferences: e.target?.checked ? 1 : editLotteryModal?.timePreferences,
                  })
                }
              />
              <Toggle
                size="medium"
                labelTop="Allow Guests"
                checked={!!editLotteryModal?.allowGuests}
                onChange={e => updateEditLotteryModal({ allowGuests: e.target.checked })}
              />
            </FormLayout.Group>
          </FormLayout>
        </Sheet>
      </Portal>

      {/* NO SHOW CHARGE CONFIRMATION */}
      <Portal isMounted={noShowChargeModal?.confirmationPopup}>
        <Popup
          onCancel={() => updateNoShowChargeModal({ confirmationPopup: false })}
          onOk={() => handleNoShow(true)}
          okText={t("Charge")}
          open={noShowChargeModal?.confirmationPopup}
          type="info"
          title={t("Confirm No Show Charge")}
          stacked
          description={`${t("Are you sure you want to apply a no show charge to the payment method ending in")} ${
            noShowChargeModal?.paymentMethod?.last4
          } ${t("attached to this booking?")}`}
        />
      </Portal>

      {/* CREATE SHOTGUN MODAL */}
      <Portal isMounted={createShotgunModal.isOpen}>
        <CreateShotgunModal
          open={createShotgunModal.isOpen}
          teetime={createShotgunModal.teetime}
          courses={facilityStore.facility?.courses}
          bookingCategories={bookingCategories}
          templates={shotgunTemplates}
          onOk={() => {
            closeCreateShotgunModal();
            props.reloadTeeTimeTabs();
          }}
          onCancel={closeCreateShotgunModal}
        />
      </Portal>

      {/* AUDIT TRAIL MODAL */}
      <Portal isMounted={auditTrailModal.isOpen}>
        <AuditTrailModal
          open={auditTrailModal.isOpen}
          teetime={auditTrailModal.teetime}
          bookingId={auditTrailModal.bookingId}
          onCancel={() =>
            updateAuditTrailModal({
              isOpen: false,
              teetime: null,
              type: null,
            })
          }
          type={auditTrailModal.type}
        />
      </Portal>

      {/* TEE TIME DETAILS MODAL */}
      <Portal isMounted={teeTimeDetailsModal.isOpen}>
        <TeeTimeDetailsModal
          open={teeTimeDetailsModal.isOpen}
          teetime={teeTimeDetailsModal.teetime}
          bookingCategories={bookingCategories}
          onCancel={closeTeeTimeDetailsModal}
        />
      </Portal>

      {/* ADD TEE TIME MODAL */}
      <Portal isMounted={addTeeTimeModal.isOpen}>
        <AddTeeTimeModal
          open={addTeeTimeModal.isOpen}
          teetime={addTeeTimeModal.teetime}
          onOk={() => {
            closeAddTeeTimeModal();
            props.reloadTeeTimeTabs();
          }}
          onCancel={closeAddTeeTimeModal}
        />
      </Portal>

      {/* ADD SQUEEZE TIME POPUP */}
      <Portal isMounted={addSqueezeTimeModal.isOpen}>
        <Popup
          open={addSqueezeTimeModal.isOpen}
          type="warning"
          title={t("Add Squeeze Time")}
          description={t("Are you sure you want to add a squeeze time?")}
          onOk={createSqueezeTime}
          okText={t("Add")}
          onCancel={closeAddSqueezeTimeModal}
          backDropCancel={true}
        />
      </Portal>

      {/* EDIT START HOLE MODAL */}
      <Portal isMounted={editStartHoleModal.isOpen}>
        <EditStartHoleModal
          open={editStartHoleModal.isOpen}
          teetime={editStartHoleModal.teetime}
          onOk={() => {
            closeEditStartHoleModal();
            props.reloadTeeTimeTabs();
          }}
          onCancel={closeEditStartHoleModal}
        />
      </Portal>

      {/* EDIT BOOKING MODAL */}
      <Portal isMounted={editBookingModal.isOpen}>
        <BookingModalEdit
          cartsAvailable={editBookingModal.cartsAvailable}
          selectedBooking={editBookingModal.selectedBooking}
          teeTimeLock={editBookingModal.teeTimeLock}
          refreshTeeTime={true}
          loading={editBookingModal.loading}
          loadingText={editBookingModal.loadingText}
          reloadTeeTimes={props.reloadTeeTimeTabs}
          reloadTeeTime={(updateTeetimeTabAction: TLoadingUpdateBookingAction) =>
            void props.reloadActionTeeTimeTabs(updateTeetimeTabAction, true)
          }
          openDuplicateBookingModal={(booking, teeTime) =>
            updateDuplicateBookingModal({ isOpen: true, booking, teeTime })
          }
        />
      </Portal>

      {/* PRINT CART SIGNS MODAL */}
      <Portal isMounted={printCartSignsModal.isOpen}>
        <PrintCartSignsModal
          open={printCartSignsModal.isOpen}
          teetime={printCartSignsModal.teetime}
          teetime_id={printCartSignsModal.teetime_id}
          teeTimes={props.teetimeTabs}
          tournaments={state.teeSheetTournaments}
          leagues={state.teeSheetLeagues}
          onOk={closePrintCartSignsModal}
          onCancel={closePrintCartSignsModal}
        />
      </Portal>

      {/* POWER CART NUMBERS MODAL */}
      <PowerCartNumbersModal
        open={powerCartNumbersModal.isOpen}
        participants={
          powerCartNumbersModal.teetime?.slots
            ?.filter(slot => slot.booking_participant_id)
            ?.map(slot => ({
              participant_id: slot.booking_participant_id,
              full_name: `${slot.customer_first_name ?? ""} ${slot.customer_last_name ?? ""}`.trim(),
              guest: slot.guest,
              customer_type: slot.customer_type_title,
              cart_customer_type: slot.customer_type_subtitle,
              power_cart_id: slot.power_cart_id,
              power_cart_number: slot.power_cart_number,
              seat_number: slot.seat_number,
            })) ?? []
        }
        onCancel={closePowerCartNumbersModal}
        onSaveSuccess={props.reloadTeeTimeTabs}
      />

      {/* PRINT SCORECARD MODAL */}
      <Portal isMounted={printScoreCardsModal.isOpen}>
        <PrintScoreCardsModal
          open={printScoreCardsModal.isOpen}
          teetime={printScoreCardsModal?.teetime}
          league={printScoreCardsModal?.league}
          tournament={printScoreCardsModal?.tournament}
          onOk={closePrintScoreCardsModal}
          onCancel={closePrintScoreCardsModal}
        />
      </Portal>

      {/* POPUPS */}
      {/* BLOCKED TEE TIME POPUP */}
      <Portal isMounted={blockedTeeTimePopup.isOpen}>
        <Popup
          open={blockedTeeTimePopup.isOpen}
          type="warning"
          title={t("Tee Time is blocked!")}
          description={t("Do you wish to continue with a new booking?")}
          onOk={() => {
            closeBlockedTeeTimePopup();
            void handleInitiateBooking(state.selectedTeeTime);
          }}
          okText={t("Continue")}
          onCancel={closeBlockedTeeTimePopup}
          backDropCancel={true}
        />
      </Portal>

      {/* SELECT DIVISIONS POPUP */}
      <Portal isMounted={selectDivisionsPopup.isOpen}>
        <Popup
          open={selectDivisionsPopup.isOpen}
          type="info"
          title={t("Select Course Divisions")}
          description={t("Select front and back holes for scorecard")}
          onOk={() => {
            closeSelectDivisionsPopup();
            void handlePrintScorecards();
          }}
          okText={t("Print")}
          onCancel={closeSelectDivisionsPopup}
          backDropCancel={true}
        >
          <div className="flex gap-5 mt-3">
            <div className="w-full">
              <Select
                label={t("Front Nine")}
                onChange={(value: string) => updateSelectDivisionsPopup({ selectedFrontNineDivision: value })}
              >
                {facilityStore?.facility?.courses[0]?.divisions?.map((division: any, index: number) => {
                  return (
                    <Select.Option value={division.id} name={division.title} key={index}>
                      <span>{division.title}</span>
                    </Select.Option>
                  );
                })}
              </Select>
            </div>

            <div className="w-full">
              <Select
                label={t("Back Nine")}
                onChange={(value: string) => updateSelectDivisionsPopup({ selectedBackNineDivision: value })}
              >
                {facilityStore?.facility?.courses[0]?.divisions?.map((division: any, index: number) => {
                  return (
                    <Select.Option value={division.id} name={division.title} key={index}>
                      <span>{division.title}</span>
                    </Select.Option>
                  );
                })}
              </Select>
            </div>
          </div>
        </Popup>
      </Portal>

      {/* CANCEL BOOKING POPUP */}
      <Portal isMounted={cancelBookingPopup.isOpen}>
        <Popup
          open={cancelBookingPopup.isOpen}
          type="warning"
          title={t("Cancel Booking?")}
          description={`${t("Cancel booking on")} ${cancelBookingPopup?.teetimeToCancelDate} at ${
            cancelBookingPopup?.teetimeToCancelTime
          }?`}
          onOk={cancelBooking}
          okText={t("Cancel Booking")}
          onCancel={() =>
            updateCancelBookingPopup({
              isOpen: false,
              teetimeToCancelDate: "",
              teetimeToCancelTime: "",
              teetimeToCancelBookingId: null,
            })
          }
          backDropCancel={false}
        >
          <div className="mt-3">
            <Checkbox
              id={"send_cancellation_email"}
              size="small"
              checked={cancelBookingPopup.send_cancellation_email}
              onChange={handleCheckboxChange}
              label={t("Cancellation Email")}
            />
          </div>
        </Popup>
      </Portal>

      {/* CANCEL PLAYER POPUP */}
      <Portal isMounted={cancelPlayerPopup.isOpen}>
        <Popup
          open={cancelPlayerPopup.isOpen}
          type="warning"
          title={t("Cancel Player?")}
          description={`${t("Are you sure you want to cancel")} ${cancelPlayerPopup.bookingParticipantName}?`}
          onOk={cancelBookingParticipant}
          okText={t("Cancel Player")}
          onCancel={() =>
            updateCancelPlayerPopup({
              isOpen: false,
              bookingParticipantName: "",
              bookingParticipantId: null,
            })
          }
          backDropCancel={false}
        />
      </Portal>

      {/* TEE TIME BOOKED POPUP */}
      <Portal isMounted={teeTimeBookedPopup.isOpen}>
        <Popup
          open={teeTimeBookedPopup.isOpen}
          type="warning"
          title={t("Tee Time is Booked!")}
          description={t("This tee time is fully booked. Select continue to refresh the Tee Sheet.")}
          onOk={closeTeeTimeBookedPopup}
          okText={t("Continue")}
          onCancel={closeTeeTimeBookedPopup}
          backDropCancel={false}
        />
      </Portal>

      {/* TEE TIME LOCKED POPUP */}
      <Portal isMounted={teeTimeLockedPopup.isOpen}>
        <Popup
          open={teeTimeLockedPopup.isOpen}
          type="warning"
          title={t("Tee Time Locked!")}
          description={t("This tee time is currently being booked by another user.")}
          onOk={closeTeeTimeLockedPopup}
          okText={t("Continue")}
          onCancel={closeTeeTimeLockedPopup}
          backDropCancel={true}
        />
      </Portal>

      {/* REMOVE TEE TIME POPUP */}
      <Portal isMounted={removeTeeTimePopup.isOpen}>
        <Popup
          open={removeTeeTimePopup.isOpen}
          stacked
          type="warning"
          title={t("Remove Tee Time")}
          description={t("Are you sure you want to remove this tee time? This cannot be undone")}
          onOk={removeTeeTime}
          okText={t("Remove")}
          onCancel={closeRemoveTeeTimePopup}
          backDropCancel={false}
        />
      </Portal>

      {/* REMOVE SHOTGUN POPUP */}
      <Portal isMounted={removeShotgunPopup.isOpen}>
        <Popup
          open={removeShotgunPopup.isOpen}
          stacked
          type="warning"
          title={t("Remove Shotgun Template")}
          description={t(
            "Are you sure you want to remove shotgun template? All original tee times will be re-enabled and marked as blocked. Blocked type will have to be manually reset.",
          )}
          onOk={removeShotgunTemplate}
          okText={t("Remove")}
          onCancel={closeRemoveShotgunPopup}
          backDropCancel={false}
        />
      </Portal>

      {/* EDIT SHOTGUN MODAL */}
      <EditShotgunModal
      open={editShotgunPopup?.isOpen}
      teetime={editShotgunPopup?.teetime}
      onClose={closeEditShotgunPopup}
      teetimes={props.teetimeTabs}
      onOk={handleEditShotgun}
      />

      <ImportDrawSheetModal
        onCancel={closeFileImportModal}
        onOk={(selectedFile: File, customer_id: number) => {
          void importDrawsheet(selectedFile, customer_id);
        }}
        open={fileImportModal.isOpen}
      />

      <ImportPlayersFileModal
        onCancel={closeImportPlayersFileModal}
        onOk={(selectedFile: File, customer_id: number, holes) =>
          void importPlayersFile(selectedFile, customer_id, holes)
        }
        open={importPlayersFileModal?.isOpen}
        organizer={importPlayersFileModal?.organizer}
        type={importPlayersFileModal?.tournament_id ? "tournament" : "league"}
      />

      <AddTournamentPlayerModal
        onCancel={closeAddTournamentPlayer}
        onOk={(name: string, customer_id: number) => {
          void addTournamentPlayer(name, customer_id, addTournamentPlayerModal?.slot);
        }}
        open={addTournamentPlayerModal?.isOpen}
        organizer={addTournamentPlayerModal?.organizer}
      />

      {/* CHECK IN SHOTGUN PARTICIPANTS */}
      <Portal isMounted={shotgunCheckInParticipants.isOpen}>
        <Popup
          open={shotgunCheckInParticipants.isOpen}
          stacked
          type="warning"
          title={t("Check In All Participants")}
          description={t("Are you sure you want to check in all participants for this shotgun?")}
          onOk={checkInAllShotgunParticipants}
          okText={t("Check In")}
          onCancel={closeCheckInAllShotgunParticipants}
          backDropCancel={false}
        />
      </Portal>

      {/* CHECK IN TOURNAMENT PARTICIPANTS */}
      <Portal isMounted={tournamentCheckInParticipants.isOpen}>
        <Popup
          open={tournamentCheckInParticipants.isOpen}
          stacked
          type="warning"
          title={t("Check In All Participants")}
          description={t("Are you sure you want to check in all participants for this tournament?")}
          onOk={checkInAllTournamentParticipants}
          okText={t("Check In")}
          onCancel={closeCheckInAllTournamentParticipants}
          backDropCancel={false}
        />
      </Portal>

      {/* CHECK IN LEAGUE PARTICIPANTS */}
      <Portal isMounted={leagueCheckInParticipants.isOpen}>
        <Popup
          open={leagueCheckInParticipants.isOpen}
          stacked
          type="warning"
          title={t("Check In All Participants")}
          description={t("Are you sure you want to check in all participants for this league?")}
          onOk={checkInAllLeagueParticipants}
          okText={t("Check In")}
          onCancel={closeCheckInAllLeagueParticipants}
          backDropCancel={false}
        />
      </Portal>

      {/* BUILD LOTTERY */}
      <Portal isMounted={buildLotteryPopup.isOpen}>
        <Popup
          open={buildLotteryPopup.isOpen}
          stacked
          type="info"
          title={t("Build Lottery")}
          description={t("Are you sure you want to build the lottery for these tee times?")}
          onOk={handleBuildLottery}
          okText={t("Build")}
          onCancel={closeBuildLotteryPopup}
          backDropCancel={false}
        />
      </Portal>

      {/* DELETE LOTTERY REQUEST*/}
      <Portal isMounted={deleteLotteryRequestPopup.isOpen}>
        <Popup
          open={deleteLotteryRequestPopup.isOpen}
          stacked
          type="warning"
          title={t("Delete Lottery Request")}
          description={t(
            `Are you sure you want to delete the lottery request of ${deleteLotteryRequestPopup?.request?.customer_name_one}?`,
          )}
          onOk={deleteLotteryRequest}
          okText={t("Delete")}
          onCancel={closeDeleteLotteryRequestPopup}
          backDropCancel={false}
        />
      </Portal>

      {/* FINALIZE LOTTERY*/}
      <Portal isMounted={finalizeLotteryPopup.isOpen}>
        <Popup
          open={finalizeLotteryPopup.isOpen}
          stacked
          type="warning"
          title={t("Finalize Lottery")}
          description={t(
            "Are you sure you want to finalize this lottery? Bookings will be made for the players in this lottery build.",
          )}
          onOk={handleFinalizeLottery}
          okText={t("Finalize")}
          onCancel={closeFinalizeLotteryPopup}
          backDropCancel={false}
        />
      </Portal>

      {/* CHECK IN PLAYER POPUP */}
      <Portal isMounted={checkInPlayerPopup.isOpen}>
        <Popup
          open={checkInPlayerPopup.isOpen}
          closable
          type="info"
          title={t(
            "Check in " + checkInPlayerPopup?.bookingParticipantName + (checkInPlayerPopup.isGuest ? " (G)" : ""),
          )}
          description={t("Are you sure you want to check the player in?")}
          onOk={checkInPlayer}
          onCancel={closeCheckInPlayerPopup}
        />
      </Portal>

      <Portal isMounted={issueRainCheckModal.isOpen}>
        <IssueRainCheckModal
          open={issueRainCheckModal.isOpen}
          bookingId={issueRainCheckModal.bookingId}
          bookingParticipantId={issueRainCheckModal.bookingParticipantId}
          holes={issueRainCheckModal.holes}
          onOk={() => {
            void closeIssueRainCheckModal();
            props.reloadTeeTimeTabs();
          }}
          onCancel={closeIssueRainCheckModal}
        />
      </Portal>

      <DuplicateBookingModal
        open={duplicateBookingModal?.isOpen}
        booking={duplicateBookingModal.booking}
        teetime={duplicateBookingModal.teeTime}
        facility={facilityStore?.facility}
        onClose={refreshTeeTimes => {
          void closeDuplicateBookingModal();
          if (refreshTeeTimes) {
            props.reloadTeeTimeTabs();
          }
        }}
      />
    </>
  );
}
